import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import ImavenOnlineStatus from '../Helpers/imaven-online-status';
import { useAuth0 } from '@auth0/auth0-react';
import * as S from './UserListStyles';
import { useToast } from '../../Context/ToastContext';
import { useConfirm } from '../../Context/ConfirmContext';

import SearchIcon from '../../Images/search-icon.svg';
import FilterIcon from '../../Images/filter-icon.svg';
import InfoIcon from '../../Images/info-outline.svg';
import ConversationIcon from '../../Images/chat-conversation.svg';
import AllFilesIcon from '../../Images/all-files.svg';
import RemoveIcon from '../../Images/remove.svg';
import ConcludeIcon from '../../Images/conclude.svg';
import UserListIcon from '../../Images/userlist-icon.svg';
import AssignIcon from '../../Images/assign-icon.svg';
import { onUserConnected, onUserDisconnected, onUserListReceived, sendRequestUserList, sendConcludeMessage } from '../../Socket/socket';
import { ConversationFilesPopup } from '../ConversationFiles';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import useLeadLabels from './hooks/useLeadLabels';
import useSendEmail from './hooks/useSendEmail';
import useAssignments from './hooks/useAssignments';
import { getSubcontractorLinks, shareCaseWithSubcontractor } from '../../services/subcontractorService';
import { getSharingSummary, getCaseSharing, revokeSubcontractorGrant, cancelReferral } from '../../services/sharingService';

// Add a static cache for the user list data and loading state tracker
// This will ensure we keep a copy of the user list data even when component remounts
const userListCache = {
  data: null,
  timestamp: null,
  isCurrentlyLoading: true, // Track global loading state
  // Track if filters should be preserved between navigations
  shouldPreserveFilters: false,
  // Add a dedicated mapping of user IDs to online status
  userStatusMap: {}
};

// Add a mapping to track disabled conclude buttons by threadId
// This allows the disabled state to persist even when component remounts
const concludeDisabledTimers = {};

// Try to load cached online status from localStorage
try {
  const savedStatus = localStorage.getItem('userStatusMap');
  if (savedStatus) {
    userListCache.userStatusMap = JSON.parse(savedStatus);
    console.log('Restored online status from localStorage:', userListCache.userStatusMap);
  }
} catch (e) {
  console.error('Error restoring online status from localStorage:', e);
}

// Add this function near the top of your component
const stripHtml = (html) => {
  if (!html) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
};

const AVATAR_COLORS = [
  '#6366f1', '#8b5cf6', '#a855f7', '#ec4899', '#ef4444',
  '#f97316', '#eab308', '#22c55e', '#14b8a6', '#06b6d4',
  '#3b82f6', '#6d28d9', '#db2777', '#059669', '#0891b2',
];

const getInitials = (name) => {
  if (!name || name === 'Widget User' || name === 'widget') return '?';
  const clean = name.replace(/\(inferred\)/gi, '').trim();
  if (clean.includes('@')) return clean[0];
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0];
  return parts[0][0] + parts[parts.length - 1][0];
};

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const UserAvatar = ({ name, userIcon, size, radius, fontSize, sizeSm, fontSizeSm }) => {
  const [imgFailed, setImgFailed] = React.useState(false);
  
  if (userIcon && !imgFailed) {
    return (
      <S.UserImage
        src={userIcon}
        alt="User avatar"
        style={size ? { width: size, height: size, borderRadius: radius || '8px' } : undefined}
        onError={() => setImgFailed(true)}
      />
    );
  }
  const initials = getInitials(name);
  return (
    <S.InitialsAvatar
      $bg={getAvatarColor(name)}
      $size={size}
      $radius={radius}
      $fontSize={fontSize}
      $sizeSm={sizeSm}
      $fontSizeSm={fontSizeSm}
    >
      {initials}
    </S.InitialsAvatar>
  );
};

// Lead-row chip styling per widget assessment type.
const ASSESSMENT_CHIP_META = {
  crs: { label: 'CRS', icon: '🎯', title: 'Express Entry CRS', colour: '#7dd3fc', bg: 'rgba(56, 189, 248, 0.15)' },
  oinp_candidate: { label: 'ON PNP', icon: '🍁', title: 'Ontario Workforce Priority (EOI)', colour: '#fcd34d', bg: 'rgba(252, 211, 77, 0.15)' },
  oinp_employer: { label: 'Employer', icon: '🏢', title: 'OINP employer eligibility', colour: '#86efac', bg: 'rgba(134, 239, 172, 0.15)' },
};

const UserList = ({
  conversationsInit, 
  clientId, 
  onConversationSelect, 
  loadingData, 
  threadIdParam = '', 
  selectedUserOnNavigation = null,
  // New props for incomplete leads feature
  incompleteConversations = [],
  incompleteLoading = false,
  onHideSession,
  onUnhideSession,
  onFetchIncomplete,
  onIncompleteConversationSelect,
  // New prop for manual lead creation
  onAddLead,
  // Refresh the main Leads list (after a ZIP import creates a lead)
  onRefreshConversations,
  // Incomplete session actions
  onGenerateSignupLink,
  onConvertToLead,
  onGenerateSignupLinkForLead,
  onSendSignupEmail
}) => {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  
  // Tab state for Leads vs Incomplete
  const [activeTab, setActiveTab] = useState('leads');
  const [showHiddenIncomplete, setShowHiddenIncomplete] = useState(false);
  const [inactiveThreshold, setInactiveThreshold] = useState(60);
  const [selectedIncompleteId, setSelectedIncompleteId] = useState(null);
  const [incompleteMenuSession, setIncompleteMenuSession] = useState(null);
  const [incompleteMenuPosition, setIncompleteMenuPosition] = useState({ top: 0, left: 0 });
  const [signupLinkPopup, setSignupLinkPopup] = useState({ visible: false, link: '', loading: false, email: '', userName: '', sendingEmail: false, emailSent: false, emailError: '' });
  
  // Track if this is first mount to reset filters on new page loads
  const [isFirstMount, setIsFirstMount] = useState(true);
  // Always start with loading true on fresh component mount
  const [isLoading, setIsLoading] = useState(true);
  const [conversations, setConversations] = useState(userListCache.data || conversationsInit || []);
  // Add a dedicated state for tracking user online status - initialize from cache
  const [userStatus, setUserStatus] = useState(userListCache.userStatusMap || {});
  const [filter, setFilter] = useState('');
  const { user, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const authenticatedAxios = useAuthenticatedAxios();
  
  const [FilteredList, setFilterdList] = useState(userListCache.data || conversationsInit || []);
  const scrollContainerRef = useRef(null); // Ref to preserve scroll position
  const safetyTimerRef = useRef(null);
  const socketTimersRef = useRef([]);
  const applyFiltersRef = useRef(null);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [selectedUser, setSelectedUser] = useState();
  const [threadId, setThreadId] = useState(threadIdParam);
  const [isFilterPopupVisible, setIsFilterPopupVisible] = useState(false);
  const [isInfoPopupVisible, setIsInfoPopupVisible] = useState(false);
  const [infoPopupPosition, setInfoPopupPosition] = useState({ top: 0, left: 0 });
  const [selectedConversationDetails, setSelectedConversationDetails] = useState(null);
  const [filterSettings, setFilterSettings] = useState({
    showOnlineOnly: false,
    activityPeriod: 'all',
  });
  const [isConversationHistoryVisible, setIsConversationHistoryVisible] = useState(false);
  const [conversationHistoryPosition, setConversationHistoryPosition] = useState({ top: 0, left: 0 });
  const [isRemoveConfirmVisible, setIsRemoveConfirmVisible] = useState(false);
  const [removeConfirmPosition, setRemoveConfirmPosition] = useState({ top: 0, left: 0 });
  const [areFiltersActive, setAreFiltersActive] = useState(false);
  const filterSettingsRef = useRef(filterSettings);
  const areFiltersActiveRef = useRef(areFiltersActive);
  const [isFilesPopupVisible, setIsFilesPopupVisible] = useState(false);
  // Add a data loaded flag to track first successful load
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  // Add a flag to prevent flashing empty message
  const [shouldShowEmptyMessage, setShouldShowEmptyMessage] = useState(false);
  // Add state to track which conclude buttons are disabled
  const [disabledConcludeButtons, setDisabledConcludeButtons] = useState({});
  
  // Add Lead modal state
  const [isAddLeadModalVisible, setIsAddLeadModalVisible] = useState(false);
  const [addLeadForm, setAddLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [isAddingLead, setIsAddingLead] = useState(false);
  const [addLeadSuccess, setAddLeadSuccess] = useState(null); // { signupLink, email }

  // Add Old User (legacy ZIP import) modal state
  const [isAddOldUserModalVisible, setIsAddOldUserModalVisible] = useState(false);
  const [importFile, setImportFile] = useState(null);          // the chosen .zip File
  const [importJob, setImportJob] = useState(null);            // polled job status
  const [isUploadingImport, setIsUploadingImport] = useState(false);
  const [linkSelections, setLinkSelections] = useState({});    // requirementKey -> bool
  const importPollActiveRef = useRef(false);
  const importFileInputRef = useRef(null);
  const importLogRef = useRef(null);

  // Analyzed name overrides for conversations with generic names (e.g. "Widget User")
  const [nameOverrides, setNameOverrides] = useState({});

  // --- Subcontractor sharing (per-lead "Share" action) ---
  // shareMenuThreadId: threadId of the row whose share-picker is open (null = none)
  // subcontractors: null = not yet fetched, [] = fetched but none active
  const [shareMenuThreadId, setShareMenuThreadId] = useState(null);
  const [subcontractors, setSubcontractors] = useState(null);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [isShareSubmenuVisible, setIsShareSubmenuVisible] = useState(false);

  // --- "Shared with" (owner-side view of who a case is shared with) ---
  // sharingSummary: { [threadId]: { subs, referrals, total } } — batch-loaded once.
  // sharedWithPanel: the open detail panel for one case.
  const [sharingSummary, setSharingSummary] = useState({});
  // threadId -> [{ type, verdict, score, maxScore, scoreState, ... }]
  const [assessmentsByThread, setAssessmentsByThread] = useState({});
  const [sharedWithPanel, setSharedWithPanel] = useState({ visible: false, threadId: null, name: '', loading: false, shares: [], counts: null });
  const [revokingId, setRevokingId] = useState(null);

  // --- Custom hooks ---
  const {
    leadLabels, setLeadLabels,
    threadLabelMap, setThreadLabelMap,
    labelPickerThreadId, setLabelPickerThreadId,
    labelPickerPosition, setLabelPickerPosition,
    isEditLabelsVisible, setIsEditLabelsVisible,
    editLabelsData, setEditLabelsData,
    labelFilterValues, setLabelFilterValues, toggleLabelFilter, clearLabelFilter,
    getLeadLabelForThread, getLabelConfig,
    handleSetLeadLabel, openLabelPicker, handleSaveLabels,
  } = useLeadLabels({ clientId, authenticatedAxios, conversations, incompleteConversations, showToast });

  const {
    isSendEmailModalVisible, setIsSendEmailModalVisible,
    sendEmailRecipient, setSendEmailRecipient,
    sendEmailForm, setSendEmailForm,
    isSendingEmail, setIsSendingEmail,
    sendEmailStatus, setSendEmailStatus,
    signatureTemplates, setSignatureTemplates,
    selectedSignatureTemplates, setSelectedSignatureTemplates,
    loadingTemplates, setLoadingTemplates,
    docuSignConnected, setDocuSignConnected,
    openSendEmailModal,
    handleSendEmailFormChange, toggleSignatureTemplate,
    handleSendEmail, closeSendEmailModal,
  } = useSendEmail({ authenticatedAxios, getAccessTokenSilently, showToast, user });

  const {
    assignablePeople, setAssignablePeople,
    customerAssignments, setCustomerAssignments,
    isAssignSubmenuVisible, setIsAssignSubmenuVisible,
    isAddPersonModalVisible, setIsAddPersonModalVisible,
    newPersonName, setNewPersonName,
    newPersonEmail, setNewPersonEmail,
    newPersonRole, setNewPersonRole,
    isAddingPerson, setIsAddingPerson,
    isAssigning, setIsAssigning,
    handleAssignCustomer, handleAddPerson, getAssignmentForThread,
  } = useAssignments({ isAuthenticated, authenticatedAxios, conversations, threadId, showToast, setIsPopupVisible });

  // Save user status to cache and localStorage when it changes
  useEffect(() => {
    userListCache.userStatusMap = userStatus;

    // Also save to localStorage for persistence across browser refreshes
    try {
      localStorage.setItem('userStatusMap', JSON.stringify(userStatus));
      console.log('Saved online status to localStorage');
    } catch (e) {
      console.error('Error saving online status to localStorage:', e);
    }
  }, [userStatus]);
  
  // Initialize disabled conclude buttons on mount
  useEffect(() => {
    // Check if any timers are active and restore their state
    const activeDisabled = {};
    Object.keys(concludeDisabledTimers).forEach(threadId => {
      activeDisabled[threadId] = true;
    });
    
    if (Object.keys(activeDisabled).length > 0) {
      setDisabledConcludeButtons(activeDisabled);
      console.log('Restored disabled conclude buttons:', activeDisabled);
    }
  }, []);

  // Fetch incomplete conversations when switching to incomplete tab
  useEffect(() => {
    if (activeTab === 'incomplete' && onFetchIncomplete) {
      onFetchIncomplete(showHiddenIncomplete, inactiveThreshold);
    }
  }, [activeTab, showHiddenIncomplete, inactiveThreshold, onFetchIncomplete]);

  const isGenericName = (name) => !name || name === 'Widget User' || name === 'widget' || name === 'Unknown';

  // Fetch analyzed client names for conversations with generic names
  useEffect(() => {
    let cancelled = false;
    const fetchAnalyzedNames = async () => {
      if (!conversations?.length) return;
      const genericThreadIds = conversations
        .filter(c => isGenericName(c.name) && c.threadId)
        .map(c => c.threadId);
      if (genericThreadIds.length === 0) return;
      try {
        const response = await authenticatedAxios.post('/analysis/client-names', { threadIds: genericThreadIds });
        if (!cancelled && response.data?.names) {
          setNameOverrides(prev => ({ ...prev, ...response.data.names }));
        }
      } catch (error) {
        console.error('Failed to fetch analyzed client names:', error);
      }
    };
    fetchAnalyzedNames();
    return () => { cancelled = true; };
  }, [conversations, authenticatedAxios]);

  // Which leads arrived having already completed a widget assessment (CRS,
  // Ontario PNP, or the employer check). One batch request rather than N+1 as the
  // list grows, mirroring the client-names fetch above.
  useEffect(() => {
    let cancelled = false;
    const fetchAssessments = async () => {
      const threadIds = (conversations || []).map(c => c.threadId).filter(Boolean);
      if (threadIds.length === 0) return;
      try {
        const response = await authenticatedAxios.post('/assessments/batch', { threadIds });
        if (!cancelled && response.data?.byThreadId) {
          setAssessmentsByThread(response.data.byThreadId);
        }
      } catch (error) {
        // A lead with no assessment is the common case; never surface this.
        if (!cancelled) setAssessmentsByThread({});
      }
    };
    fetchAssessments();
    return () => { cancelled = true; };
  }, [conversations, authenticatedAxios]);

  const isInvalidName = (name) => {
    if (!name) return true;
    const lower = name.toLowerCase().trim();
    return ['undefined', 'undefined undefined', 'null', 'null null', 'n/a', 'unknown', 'not provided', 'none'].includes(lower);
  };

  const getDisplayName = (conversation) => {
    if (conversation?.threadId && nameOverrides[conversation.threadId]) {
      const override = nameOverrides[conversation.threadId];
      if (!isInvalidName(override)) return override;
    }
    const name = conversation?.name;
    if (name && !isInvalidName(name)) return name;
    return conversation?.email || 'Unknown';
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle hide session
  const handleHideSession = async (sessionId) => {
    if (onHideSession) {
      try {
        await onHideSession(sessionId);
        showToast('Session hidden successfully', 'success');
      } catch (error) {
        console.error('Failed to hide session:', error);
        showToast('Failed to hide session. Please try again.', 'error');
      }
    }
  };

  // Handle unhide session
  const handleUnhideSession = async (sessionId) => {
    if (onUnhideSession) {
      try {
        await onUnhideSession(sessionId);
        showToast('Session restored successfully', 'success');
      } catch (error) {
        console.error('Failed to restore session:', error);
        showToast('Failed to restore session. Please try again.', 'error');
      }
    }
  };

  // Format relative time for incomplete leads
  const formatRelativeTime = (date) => {
    if (!date) return '';
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Reset filters on first mount (when navigating to the page)
  useEffect(() => {
    if (isFirstMount && !userListCache.shouldPreserveFilters) {
      // Reset filters to default when initially mounting component
      setFilter('');
      setFilterSettings({
        showOnlineOnly: false,
        activityPeriod: 'all',
      });
      clearLabelFilter();
      setAreFiltersActive(false);

      // Mark as no longer first mount
      setIsFirstMount(false);
    }
  }, [isFirstMount]);

  // When isLoading changes, update the global cache
  useEffect(() => {
    userListCache.isCurrentlyLoading = isLoading;
  }, [isLoading]);

  // Simplified loading behaviour: remain in the loading state until data arrives,
  // then remove the spinner immediately. If nothing arrives after 10 s we fall
  // back to an empty-state message.
  useEffect(() => {
    // If we already have conversations (prop or cache) stop loading instantly
    if (conversations && conversations.length > 0) {
      setIsLoading(false);
      return;
    }

    //   otherwise keep spinner and schedule a single safety timeout
    const safetyTimer = setTimeout(() => {
      setIsLoading(false);
      setShouldShowEmptyMessage(true);
      console.warn('UserList: timed-out waiting for user list (10 s).');
    }, 10000);

    return () => clearTimeout(safetyTimer);
  }, [conversations]);

  // Update cache whenever conversations change
  useEffect(() => {
    if (conversations && conversations.length > 0) {
      userListCache.data = conversations;
      userListCache.timestamp = Date.now();
    }
  }, [conversations]);

  useEffect(() => {
    if (threadIdParam) {
      setThreadId(threadIdParam);
    }
  }, [threadIdParam]);

  // When conversationsInit changes
  useEffect(() => {
    // Always set loading to true when receiving new conversationsInit props
    setIsLoading(true);
    // Don't show empty message during loading
    setShouldShowEmptyMessage(false);

    if (conversationsInit && conversationsInit.length > 0) {
      // Sort conversations by date (newest first)
      const sortedConversations = [...conversationsInit].sort((a, b) => {
        return new Date(b.conversationStart) - new Date(a.conversationStart);
      });

      setConversations(sortedConversations);
      // Always show all conversations on initial load
      setFilterdList(sortedConversations);

      // If selectedUserOnNavigation is provided, find and set the corresponding user
      if (selectedUserOnNavigation && typeof selectedUserOnNavigation === 'object') {
        setSelectedUser(selectedUserOnNavigation);
      }

      // Only apply filters if there are active filters
      if (hasActiveFilters()) {
        applyFilters();
      }

      setInitialDataLoaded(true);

      // Data ready – stop loading immediately
      setIsLoading(false);
      setShouldShowEmptyMessage(true);
    } else if (Array.isArray(conversationsInit) && conversationsInit.length === 0) {
      setIsLoading(true);
      setShouldShowEmptyMessage(false);
    } else if (userListCache.data && userListCache.data.length > 0) {
      // If we have cached data, use it but still request fresh data
      // Sort the cached data as well
      const sortedCachedData = [...userListCache.data].sort((a, b) => {
        return new Date(b.conversationStart) - new Date(a.conversationStart);
      });

      setConversations(sortedCachedData);
      setFilterdList(sortedCachedData);
      setInitialDataLoaded(true);
      // Cached data available – no need for extra delay
      setIsLoading(false);
      setShouldShowEmptyMessage(true);
    } else {
      // Only force a user list request if we don't have cached data
      setIsLoading(true);
      sendRequestUserList();

      // Still need to eventually show empty state if data never arrives
      safetyTimerRef.current = setTimeout(() => {
        if (!initialDataLoaded) {
          setIsLoading(false);
          setShouldShowEmptyMessage(true);
        }
      }, 10000); // After 10 seconds with no data, give up and show empty
    }

    return () => {
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
    };
  }, [conversationsInit]);

  // Keep refs in sync so socket handlers always see latest values
  useEffect(() => { filterSettingsRef.current = filterSettings; }, [filterSettings]);
  useEffect(() => { areFiltersActiveRef.current = areFiltersActive; }, [areFiltersActive]);

  // Restore socket connections and online status handling - FIXED VERSION
  useEffect(() => {
    console.log('Setting up socket handlers and requesting user list');

    // Always request the user list immediately on component mount
    // This ensures we get online users as soon as possible after page refresh
    const initialTimer = setTimeout(() => {
      console.log('Sending initial request for user list');
      sendRequestUserList();
    }, 100); // Small delay to ensure socket is connected

    // Additional request after a short delay to catch any users that connected just after
    const followUpTimer = setTimeout(() => {
      console.log('Sending follow-up request for user list');
      sendRequestUserList();
    }, 1000);

    const cleanupUserConnected = onUserConnected((data) => {
      console.log('User connected event:', data);

      if (data && data.userId) {
        // Update both the state and the cache
        setUserStatus(prev => {
          const updated = {
            ...prev,
            [data.userId]: true
          };

          // Update cache immediately
          userListCache.userStatusMap = updated;

          return updated;
        });

        // Force a filter reapplication to show the user as online
        const t = setTimeout(() => {
          if (areFiltersActiveRef.current) {
            applyFiltersRef.current?.();
          }
        }, 100);
        socketTimersRef.current.push(t);
      }
    });

    const cleanupUserDisconnected = onUserDisconnected((data) => {
      console.log('User disconnected event:', data);

      if (data && data.userId) {
        // Update both the state and the cache
        setUserStatus(prev => {
          const updated = {
            ...prev,
            [data.userId]: false
          };

          // Update cache immediately
          userListCache.userStatusMap = updated;

          return updated;
        });

        // Force a filter reapplication if the online filter is active
        if (filterSettingsRef.current.showOnlineOnly) {
          const t = setTimeout(() => applyFiltersRef.current?.(), 100);
          socketTimersRef.current.push(t);
        }
      }
    });

    return () => {
      clearTimeout(initialTimer);
      clearTimeout(followUpTimer);
      socketTimersRef.current.forEach(t => clearTimeout(t));
      socketTimersRef.current = [];
      cleanupUserConnected();
      cleanupUserDisconnected();
    };
  }, []);

  // Enhanced user list received handler with better logging and direct status updates
  useEffect(() => {
    const cleanupUserListReceived = onUserListReceived((data) => {
      console.log('Connected Users from server:', data);

      if (data && Array.isArray(data)) {
        // Get all user IDs from the current user list for easier lookup
        const onlineUserIds = data.reduce((acc, onlineUser) => {
          if (onlineUser && onlineUser.userId) {
            acc[onlineUser.userId] = true;
          }
          return acc;
        }, {});

        console.log('Online user IDs from server:', Object.keys(onlineUserIds));

        // Update the status state and cache in one operation
        setUserStatus(prev => {
          // Create a new object for immutability
          const newStatus = { ...prev };

          // First, update all users that are explicitly online
          Object.keys(onlineUserIds).forEach(userId => {
            newStatus[userId] = true;
          });

          // Update the cache immediately for persistence
          userListCache.userStatusMap = newStatus;

          console.log('Updated user status with online users:', newStatus);
          return newStatus;
        });

        // Force a re-application of filters to update the display
        setTimeout(() => {
          if (areFiltersActive) {
            applyFiltersRef.current?.();
          }
        }, 100);
      }
    });

    return () => {
      cleanupUserListReceived();
    };
  }, [areFiltersActive]);

  // Set up a periodic refresh for the user list to ensure we always have up-to-date online status
  useEffect(() => {
    console.log('Setting up periodic refresh for online status');

    // Function to refresh user list
    const refreshUserList = () => {
      console.log('Periodic refresh: requesting updated user list');
      sendRequestUserList();
    };

    // Refresh every 30 seconds
    const refreshInterval = setInterval(refreshUserList, 30000);

    // Initial refresh
    refreshUserList();

    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  // Helper function to get a user's online status with more reliable fallbacks
  const getUserOnlineStatus = (userId) => {
    // First check state, then check cache, default to false
    const isOnline = userStatus[userId] === true || userListCache.userStatusMap[userId] === true;
    return isOnline;
  };

  // Debug online status tracking - add this to help diagnose issues
  useEffect(() => {
    const onlineCount = Object.values(userStatus).filter(status => status === true).length;
    console.log(`Current online users count: ${onlineCount}`);

    if (conversations && conversations.length > 0) {
      const onlineUsers = conversations.filter(user => getUserOnlineStatus(user.id));
      console.log(`Users who appear online in the list: ${onlineUsers.length}`);
      if (onlineUsers.length > 0) {
        console.log('Online user names:', onlineUsers.map(u => u.name || u.userName).join(', '));
      }
    }
  }, [userStatus, conversations]);

  // Restore click outside handler
  useEffect(() => {
    setFilterdList(conversations);

    // Modify click outside handler
    const handleClickOutside = (event) => {
      const popupElement = document.querySelector('.popup-menu');
      const dotsElements = document.querySelectorAll('.dots-menu');
      const filterPopupElement = document.querySelector('.filter-popup');
      const filterIconElement = document.querySelector('.filter-icon');
      const infoPopupElement = document.querySelector('.info-popup');
      const conversationHistoryElement = document.querySelector('.conversation-history-popup');
      const removeConfirmElement = document.querySelector('.remove-confirm-dialog');
      const incompleteMenuElement = document.querySelector('.incomplete-popup-menu');
      const signupLinkElement = document.querySelector('.signup-link-popup');
      const sharePickerElement = document.querySelector('.share-picker');
      const shareMenuElements = document.querySelectorAll('.share-menu');

      // Check if click is outside both popup and dots
      const isOutsidePopup = popupElement && !popupElement.contains(event.target);
      const isOutsideDots = Array.from(dotsElements).every(dots => !dots.contains(event.target));
      const isOutsideFilterPopup = filterPopupElement && !filterPopupElement.contains(event.target);
      const isOutsideFilterIcon = filterIconElement && !filterIconElement.contains(event.target);
      const isOutsideInfoPopup = infoPopupElement && !infoPopupElement.contains(event.target);
      const isOutsideConversationHistory = conversationHistoryElement && !conversationHistoryElement.contains(event.target);
      const isOutsideRemoveConfirm = removeConfirmElement && !removeConfirmElement.contains(event.target);
      const isOutsideIncompleteMenu = !incompleteMenuElement || !incompleteMenuElement.contains(event.target);
      const isOutsideSignupLink = !signupLinkElement || !signupLinkElement.contains(event.target);
      const isOutsideSharePicker = !sharePickerElement || !sharePickerElement.contains(event.target);
      const isOutsideShareMenu = Array.from(shareMenuElements).every(el => !el.contains(event.target));

      if (isPopupVisible && isOutsidePopup && isOutsideDots) {
        setIsPopupVisible(false);
        setThreadId('');
      }

      if (incompleteMenuSession && isOutsideIncompleteMenu && isOutsideDots) {
        setIncompleteMenuSession(null);
      }

      if (signupLinkPopup.visible && isOutsideSignupLink) {
        setSignupLinkPopup({ visible: false, link: '', loading: false });
      }

      if (shareMenuThreadId && isOutsideSharePicker && isOutsideShareMenu) {
        setShareMenuThreadId(null);
      }

      if (isFilterPopupVisible && isOutsideFilterPopup && isOutsideFilterIcon) {
        // Apply current filters when closing the popup by clicking outside
        setIsFilterPopupVisible(false);
        applyFilters();
      }

      if (isInfoPopupVisible && isOutsideInfoPopup) {
        setIsInfoPopupVisible(false);
      }

      if (isConversationHistoryVisible && isOutsideConversationHistory) {
        setIsConversationHistoryVisible(false);
      }

      // Don't auto-close the remove confirmation dialog when clicking outside
      // This is a destructive action, so we want explicit confirm/cancel
    };

    if (isPopupVisible || isFilterPopupVisible || isInfoPopupVisible || isConversationHistoryVisible || incompleteMenuSession || signupLinkPopup.visible || shareMenuThreadId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [conversations, isPopupVisible, isFilterPopupVisible, isInfoPopupVisible, isConversationHistoryVisible, incompleteMenuSession, signupLinkPopup.visible, shareMenuThreadId, filter, filterSettings]);

  // Add this special handling for conversation click to ensure spinner shows during navigation
  const handleConversationClick = async (threadId) => {
    // Preserve scroll position before any state changes
    const savedScrollTop = scrollContainerRef.current?.scrollTop || 0;
    
    try {
      // Set loading state to true before navigation
      setIsLoading(true);
      userListCache.isCurrentlyLoading = true;
      // Mark that we shouldn't preserve filters after navigation
      userListCache.shouldPreserveFilters = false;

      setThreadId(threadId);
      
      // Restore scroll position after state update
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = savedScrollTop;
        }
      });
      loadingData();

      if (isAuthenticated && user) {
        const token = await getAccessTokenSilently();

        const response = await axios.get(`${process.env.REACT_APP_SERVER}conversations/${threadId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        console.log('Conversation details response:', response.data);

        // Use more robust user filtering logic
        const filteredUsers = response.data.users ? response.data.users.filter((user_conv) => {
          // Check for valid user object with ID and filter out current user and GPT
          const shouldInclude = user_conv && user_conv.id && user_conv.id !== user.sub && user_conv.userName !== "GPT";
          console.log(`Filtering user ${user_conv?.id || 'unknown'}: include=${shouldInclude}`);
          return shouldInclude;
        }) : [];

        console.log('Filtered users for conversation:', filteredUsers);

        if (filteredUsers.length === 0) {
          console.warn('No matching users found for conversation - trying fallbacks');
          // Try to use any user except the current user
          const anyOtherUser = response.data.users ? response.data.users.find(u => u && u.id && u.id !== user.sub) : null;

          if (anyOtherUser) {
            console.log('Using fallback user:', anyOtherUser);
            setSelectedUser(anyOtherUser);
            onConversationSelect(response.data, anyOtherUser.id);
          } else if (response.data.users && response.data.users.length > 0) {
            // Last resort: just use the first user in the array
            console.log('Last resort: using first user in array:', response.data.users[0]);
            setSelectedUser(response.data.users[0]);
            onConversationSelect(response.data, response.data.users[0].id);
          } else {
            console.error('No users found in conversation data');
          }
        } else {
          setSelectedUser(filteredUsers[0]);
          onConversationSelect(response.data, filteredUsers[0].id);
        }

        setSelectedConversationDetails(response.data);

        // Important: Reset loading state after navigation completes
        setIsLoading(false);
        userListCache.isCurrentlyLoading = false;
        
        // Restore scroll position after all state updates
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = savedScrollTop;
          }
        });
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error);
      // Make sure to reset loading state on error too
      setIsLoading(false);
      userListCache.isCurrentlyLoading = false;
      
      // Restore scroll position on error too
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = savedScrollTop;
        }
      });
    }

    // Additional safety timeout to ensure loading state is reset
    // even if there are any unexpected issues
    setTimeout(() => {
      setIsLoading(false);
      userListCache.isCurrentlyLoading = false;
    }, 1000);
  };

  const filterUserList = (searchTextEvent) => {
    const text = searchTextEvent.target.value;
    setFilter(text);

    // Directly apply filter when user types
    setTimeout(() => {
      applyFilters();
    }, 100); // Small delay to ensure state is updated
  }

  // Single source of truth for "is anything filtering the list right now".
  // Groups AND together; multi-select inside a group ORs (see labelFilterValues).
  const hasActiveFilters = () =>
    (filter && filter.length > 0) ||
    filterSettings.showOnlineOnly ||
    filterSettings.activityPeriod !== 'all' ||
    labelFilterValues.length > 0;

  // Returns the conversations matching the CURRENT filter state, without touching
  // any state. Every group ANDs with the others; multi-select inside a group ORs.
  // Also used by the popup's live "N of M match" preview, so it stays log-free.
  const computeFilteredUsers = () => {
    let filteredUsers = [...(conversations || [])];

    // Apply text search filter
    if (filter && filter.length > 0) {
      filteredUsers = filteredUsers.filter(user => {
        const displayName = getDisplayName(user);
        return displayName && displayName.toLowerCase().includes(filter.toLowerCase());
      });
    }

    // Apply lead label filter — multi-select, OR within the group:
    // a lead matches if its label is any of the selected ones ('__none__' = unlabelled).
    if (labelFilterValues.length > 0) {
      filteredUsers = filteredUsers.filter(user => {
        const label = getLeadLabelForThread(user.threadId);
        return labelFilterValues.includes(label || '__none__');
      });
    }

    // Apply online status filter using our dedicated tracking
    if (filterSettings.showOnlineOnly) {
      filteredUsers = filteredUsers.filter(user => getUserOnlineStatus(user.id));
    }

    // Apply activity period filter
    if (filterSettings.activityPeriod !== 'all') {
      const now = new Date();

      switch (filterSettings.activityPeriod) {
        case 'today':
          filteredUsers = filteredUsers.filter(user => {
            if (!user.conversationStart) return false;
            const conversationDate = new Date(user.conversationStart);
            return conversationDate.toDateString() === now.toDateString();
          });
          break;
        case 'week':
          const oneWeekAgo = new Date();
          oneWeekAgo.setDate(now.getDate() - 7);
          filteredUsers = filteredUsers.filter(user => {
            if (!user.conversationStart) return false;
            const conversationDate = new Date(user.conversationStart);
            return conversationDate >= oneWeekAgo;
          });
          break;
        case 'month':
          const oneMonthAgo = new Date();
          oneMonthAgo.setMonth(now.getMonth() - 1);
          filteredUsers = filteredUsers.filter(user => {
            if (!user.conversationStart) return false;
            const conversationDate = new Date(user.conversationStart);
            return conversationDate >= oneMonthAgo;
          });
          break;
        default:
          break;
      }
    }

    // Maintain the sort order (newest first) after filtering
    filteredUsers.sort((a, b) => {
      return new Date(b.conversationStart) - new Date(a.conversationStart);
    });

    return filteredUsers;
  };

  const applyFilters = () => {
    console.log("Starting to apply filters with settings:", filterSettings, "labels:", labelFilterValues);

    // Set loading to false since we're now processing data
    setIsLoading(false);

    // Safety check - if no conversations, don't try to filter
    if (!conversations || conversations.length === 0) {
      setFilterdList([]);
      return;
    }

    // Update the filter active state
    setAreFiltersActive(hasActiveFilters());

    const filteredUsers = computeFilteredUsers();
    console.log(`Filtered ${filteredUsers.length} of ${conversations.length} leads`);
    setFilterdList(filteredUsers);
  };
  applyFiltersRef.current = applyFilters;

  const toggleFilterPopup = (event) => {
    event.stopPropagation();
    setIsFilterPopupVisible(!isFilterPopupVisible);
  }

  // Human-readable "what is currently filtering the list", shared by the popup
  // footer and the Active Filters bar so the two can never drift apart.
  const activeFilterSummary = () => {
    const parts = [];
    if (filter) parts.push(`🔍 "${filter}"`);
    if (filterSettings.showOnlineOnly) parts.push('📶 Online only');
    if (filterSettings.activityPeriod !== 'all') {
      const periodText = filterSettings.activityPeriod === 'today' ? 'Today'
        : filterSettings.activityPeriod === 'week' ? 'Last 7 days' : 'Last 30 days';
      parts.push(`📅 ${periodText}`);
    }
    if (labelFilterValues.length > 0) {
      const names = labelFilterValues.map(v => (v === '__none__' ? 'No label' : v));
      parts.push(`🏷️ ${names.join(' or ')}`);
    }
    return parts.join(' + ');
  };

  const handleFilterChange = (key, value) => {
    setFilterSettings(prev => ({
      ...prev,
      [key]: value
    }));
    // Filter will be applied by the useEffect
  }

  const resetFilters = () => {
    const defaultSettings = {
      showOnlineOnly: false,
      activityPeriod: 'all',
    };
    setFilterSettings(defaultSettings);
    setFilter('');
    clearLabelFilter();
    setAreFiltersActive(false);
    setIsFilterPopupVisible(false);

    // Since we're resetting filters, just show all conversations, without filtering for removed ones
    setFilterdList(conversations);
  }

  const applyFilterSettings = () => {
    // Update the filter active state
    setAreFiltersActive(hasActiveFilters());

    // Close popup first
    setIsFilterPopupVisible(false);

    // Use setTimeout to ensure state is updated before applying filters
    setTimeout(() => {
      applyFilters();
    }, 0);
  }

  const OpenMoreOptions = (event, threadId) => {
    event.preventDefault();
    event.stopPropagation();

    // Close popup if clicking on the same thread
    if (threadId === threadId && isPopupVisible) {
      setIsPopupVisible(false);
      setThreadId('');
      return;
    }

    const buttonRect = event.currentTarget.getBoundingClientRect();
    const popupWidth = 150; // Match the PopupWindow width

    // Calculate position relative to viewport
    let left = buttonRect.right - popupWidth - 20; // 20px offset from the dots
    let top = buttonRect.top;

    // Ensure popup stays within viewport
    if (left + popupWidth > window.innerWidth) {
      left = window.innerWidth - popupWidth - 10;
    }
    if (left < 10) {
      left = 10;
    }

    // Check if popup would extend below viewport
    const popupHeight = 200; // Approximate height
    if (top + popupHeight > window.innerHeight) {
      top = window.innerHeight - popupHeight - 10;
    }

    setPopupPosition({ top, left });
    setIsPopupVisible(true);
    setThreadId(threadId);

    // Store the threadId for quick access in popup actions
    setThreadId(threadId);
  }

  const openIncompleteMenu = (event, session) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (incompleteMenuSession?.sessionId === session.sessionId) {
      setIncompleteMenuSession(null);
      return;
    }
    
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const popupWidth = 200;
    let left = buttonRect.right - popupWidth - 10;
    let top = buttonRect.bottom + 5;
    
    if (left + popupWidth > window.innerWidth) left = window.innerWidth - popupWidth - 10;
    if (left < 10) left = 10;
    if (top + 150 > window.innerHeight) top = buttonRect.top - 155;
    
    setIncompleteMenuPosition({ top, left });
    setIncompleteMenuSession(session);
  };

  const handleGenerateLink = async (session) => {
    setIncompleteMenuSession(null);
    setSignupLinkPopup({ visible: true, link: '', loading: true, email: session.userEmail || '', userName: session.userName || '', sendingEmail: false, emailSent: false, emailError: '' });
    
    try {
      const result = await onGenerateSignupLink(session.sessionId);
      if (result?.success) {
        setSignupLinkPopup(prev => ({ ...prev, link: result.signupLink, loading: false, email: result.userEmail || prev.email, userName: result.userName || prev.userName }));
      } else {
        showToast?.('Failed to generate link', 'error');
        setSignupLinkPopup({ visible: false, link: '', loading: false, email: '', userName: '', sendingEmail: false, emailSent: false, emailError: '' });
      }
    } catch (err) {
      showToast?.('Failed to generate link', 'error');
      setSignupLinkPopup({ visible: false, link: '', loading: false, email: '', userName: '', sendingEmail: false, emailSent: false, emailError: '' });
    }
  };

  const handleGenerateLinkForLead = async (conversation) => {
    setSignupLinkPopup({ visible: true, link: '', loading: true, email: '', userName: conversation.name || '', sendingEmail: false, emailSent: false, emailError: '' });
    
    try {
      const result = await onGenerateSignupLinkForLead(conversation.threadId);
      if (result?.success) {
        setSignupLinkPopup(prev => ({ ...prev, link: result.signupLink, loading: false, email: result.userEmail || prev.email, userName: result.userName || prev.userName }));
      } else {
        showToast?.('Failed to generate link', 'error');
        setSignupLinkPopup({ visible: false, link: '', loading: false, email: '', userName: '', sendingEmail: false, emailSent: false, emailError: '' });
      }
    } catch (err) {
      showToast?.('Failed to generate link', 'error');
      setSignupLinkPopup({ visible: false, link: '', loading: false, email: '', userName: '', sendingEmail: false, emailSent: false, emailError: '' });
    }
  };

  const handleSendSignupEmail = async () => {
    if (!signupLinkPopup.email || !signupLinkPopup.link) return;
    
    setSignupLinkPopup(prev => ({ ...prev, sendingEmail: true, emailError: '' }));
    
    try {
      const result = await onSendSignupEmail({
        email: signupLinkPopup.email,
        userName: signupLinkPopup.userName,
        expertName: user?.name || 'Your Advisor',
        signupLink: signupLinkPopup.link
      });
      
      if (result?.success) {
        setSignupLinkPopup(prev => ({ ...prev, sendingEmail: false, emailSent: true }));
        showToast?.('Email sent successfully!', 'success');
      } else {
        setSignupLinkPopup(prev => ({ ...prev, sendingEmail: false, emailError: 'Failed to send email' }));
      }
    } catch (err) {
      setSignupLinkPopup(prev => ({ ...prev, sendingEmail: false, emailError: 'Failed to send email' }));
    }
  };

  const handleConvertToLeadAction = async (session) => {
    setIncompleteMenuSession(null);
    
    try {
      const result = await onConvertToLead(session.sessionId);
      if (result?.success) {
        showToast?.('Conversation converted to lead successfully', 'success');
      } else {
        showToast?.('Failed to convert to lead', 'error');
      }
    } catch (err) {
      showToast?.('Failed to convert to lead', 'error');
    }
  };

  // Open (or toggle) the subcontractor picker for a lead I own; lazy-fetch active links once.
  const openSharePicker = async (event, conversation) => {
    event.preventDefault();
    event.stopPropagation();

    if (shareMenuThreadId === conversation.threadId) {
      setShareMenuThreadId(null);
      return;
    }
    setShareMenuThreadId(conversation.threadId);

    // Fetch the contractor's active subcontractors once, then reuse.
    if (subcontractors === null && !loadingSubs) {
      setLoadingSubs(true);
      try {
        const token = await getAccessTokenSilently();
        const links = await getSubcontractorLinks(token);
        const active = (links || []).filter(
          (link) => link.status === 'active' && link.role === 'contractor'
        );
        setSubcontractors(active);
      } catch (err) {
        console.error('Failed to load subcontractors:', err);
        setSubcontractors([]);
      } finally {
        setLoadingSubs(false);
      }
    }
  };

  // Fetch the contractor's active subcontractors once (for the ⋮ menu submenu).
  const loadSubcontractors = async () => {
    if (subcontractors !== null || loadingSubs) return;
    setLoadingSubs(true);
    try {
      const token = await getAccessTokenSilently();
      const links = await getSubcontractorLinks(token);
      setSubcontractors((links || []).filter((l) => l.status === 'active' && l.role === 'contractor'));
    } catch (err) {
      console.error('Failed to load subcontractors:', err);
      setSubcontractors([]);
    } finally {
      setLoadingSubs(false);
    }
  };

  // Share one owned case with the chosen subcontractor.
  const handleShareCase = async (event, conversation, partner) => {
    event.preventDefault();
    event.stopPropagation();
    setShareMenuThreadId(null);

    try {
      const token = await getAccessTokenSilently();
      await shareCaseWithSubcontractor(token, conversation.threadId, partner.expertId);
      showToast?.(`Shared with ${partner.name}`, 'success');
    } catch (err) {
      console.error('Failed to share case:', err);
      showToast?.('Failed to share case. Please try again.', 'error');
    }
  };

  // Batch-load "who is each of my cases shared with" once, so rows can show a
  // "→ N shared" chip without an N+1 query. Owner-scoped on the server.
  const loadSharingSummary = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const token = await getAccessTokenSilently();
      const summary = await getSharingSummary(token);
      setSharingSummary(summary || {});
    } catch (err) {
      // Non-fatal — just means no chips. Don't disturb the list.
      console.warn('Failed to load sharing summary:', err?.message);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    if (conversations && conversations.length > 0) loadSharingSummary();
  }, [conversations, loadSharingSummary]);

  // Open the "Shared with" panel for one owned case and fetch its full share list.
  const openSharedWith = async (event, conversation) => {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    const tid = conversation.threadId;
    setIsPopupVisible(false);
    setSharedWithPanel({ visible: true, threadId: tid, name: getDisplayName(conversation), loading: true, shares: [], counts: null });
    try {
      const token = await getAccessTokenSilently();
      const data = await getCaseSharing(token, tid);
      setSharedWithPanel(prev => prev.threadId === tid
        ? { ...prev, loading: false, shares: data.shares || [], counts: data.counts || null }
        : prev);
    } catch (err) {
      console.error('Failed to load sharing:', err);
      showToast?.('Failed to load sharing info', 'error');
      setSharedWithPanel(prev => prev.threadId === tid ? { ...prev, loading: false } : prev);
    }
  };

  const closeSharedWith = () => setSharedWithPanel({ visible: false, threadId: null, name: '', loading: false, shares: [], counts: null });

  // Revoke a subcontractor grant, or cancel a still-pending referral. Refresh both
  // the panel and the summary chip so every view reflects it immediately.
  const handleRevokeShare = async (share) => {
    const id = share.grantId || share.referralId || share.id;
    if (!id || revokingId) return;
    const isSub = share.type === 'subcontractor';
    const ok = await confirm({
      title: isSub ? 'Revoke access' : 'Cancel referral',
      message: isSub
        ? `${share.name} will immediately lose access to this case and be notified. Continue?`
        : `The pending referral to ${share.name} will be withdrawn. Continue?`,
      confirmText: isSub ? 'Revoke' : 'Cancel referral',
      cancelText: 'Keep',
      type: 'danger',
    });
    if (!ok) return;
    setRevokingId(id);
    try {
      const token = await getAccessTokenSilently();
      if (share.type === 'subcontractor') {
        await revokeSubcontractorGrant(token, share.grantId || id);
        showToast?.(`Access revoked for ${share.name}`, 'success');
      } else {
        await cancelReferral(token, share.referralId || id);
        showToast?.(`Referral to ${share.name} cancelled`, 'success');
      }
      // Refresh the open panel.
      const tid = sharedWithPanel.threadId;
      if (tid) {
        try {
          const data = await getCaseSharing(token, tid);
          setSharedWithPanel(prev => prev.threadId === tid ? { ...prev, shares: data.shares || [], counts: data.counts || null } : prev);
        } catch { /* keep panel as-is */ }
      }
      loadSharingSummary();
    } catch (err) {
      console.error('Failed to revoke/cancel share:', err);
      showToast?.('Failed. Please try again.', 'error');
    } finally {
      setRevokingId(null);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast?.('Link copied to clipboard!', 'success');
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showToast?.('Link copied to clipboard!', 'success');
    }
  };

  const finishConversations = async (event) => {
    // Prevent default and stop propagation if it's an event
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    try {
      // Ensure we have valid threadId
      if (!threadId) {
        console.error('Cannot conclude conversation: No threadId available');
        setIsPopupVisible(false);
        return;
      }

      // Disable the conclude button for this threadId
      setDisabledConcludeButtons(prev => ({
        ...prev,
        [threadId]: true
      }));

      // Set a timer to re-enable the button after one minute (60000ms)
      if (concludeDisabledTimers[threadId]) {
        clearTimeout(concludeDisabledTimers[threadId]);
      }
      
      concludeDisabledTimers[threadId] = setTimeout(() => {
        setDisabledConcludeButtons(prev => ({
          ...prev,
          [threadId]: false
        }));
        delete concludeDisabledTimers[threadId];
      }, 60000);

      // Get the token for API calls
      const token = await getAccessTokenSilently();

      // If selectedUser is not set, try to find it from the conversations list
      if (!selectedUser) {
        console.log('Fetching user data for conclude operation...');
        // Find the conversation in the list
        const currentConversation = conversations.find(conv => conv.threadId === threadId);

        if (currentConversation) {
          // Temporary set the selectedUser
          setSelectedUser({
            id: currentConversation.id,
            userName: currentConversation.name
          });

          // Use this user info directly for the API calls
          const userId = currentConversation.id;

          if (userId) {
            // Create the conclude message - use a one-line format compatible with translation
            const message = "Message from iMaven: Thank you for participating in this session. The session has now concluded. You will shortly receive an email containing the advice provided during this session along with a detailed summary of the entire conversation. Additionally, we value your feedback and would love to hear about your experience. You will receive another email with a feedback form to help us improve our services. We greatly appreciate your trust in iMaven and look forward to assisting you again in the future. Thank you, and we wish you success in achieving your goals!";

            // First close the popup to give immediate feedback
            setIsPopupVisible(false);

            // Send the conclude message via socket
            sendConcludeMessage({
              content: message,
              threadId: threadId,
              receiverId: userId,
              senderId: user.sub,
              type: "system",
              time: new Date().toISOString()
            });

            // Call the API endpoint to finish the conversation
            await axios.post(`${process.env.REACT_APP_SERVER}finishConversation`, {
              threadId: threadId,
              userId: userId
            }, {
              headers: {
                Authorization: `Bearer ${token}`
              }
            });

            console.log('Conversation concluded successfully');
            return;
          }
        }
      }

      // If we got this far, we have a selectedUser
      // Create the conclude message - use a one-line format compatible with translation
      const message = "Message from iMaven: Thank you for participating in this session. The session has now concluded. You will shortly receive an email containing the advice provided during this session along with a detailed summary of the entire conversation. Additionally, we value your feedback and would love to hear about your experience. You will receive another email with a feedback form to help us improve our services. We greatly appreciate your trust in iMaven and look forward to assisting you again in the future. Thank you, and we wish you success in achieving your goals!";

      // First close the popup to give immediate feedback
      setIsPopupVisible(false);

      // Send conclude message
      sendConcludeMessage({
        content: message,
        threadId: threadId,
        receiverId: selectedUser.id,
        senderId: user.sub,
        type: "system",
        time: new Date().toISOString()
      });

      // Call API to finish conversation
      await axios.post(`${process.env.REACT_APP_SERVER}finishConversation`, {
        threadId: threadId,
        userId: selectedUser.id
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('Conversation concluded successfully');
    } catch (error) {
      console.error('Error concluding conversation:', error);
      // Make sure popup closes even if there's an error
      setIsPopupVisible(false);
      
      // Even in case of error, don't allow the user to click again for 1 minute
      // as the operation might have succeeded silently
    }
  }

  const showUserInfo = async (event) => {
    event.stopPropagation();

    if (!threadId) {
      return;
    }

    // Position the info popup centered on screen
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const popupWidth = 380;
    const popupHeight = 450;

    const left = Math.max(20, (viewportWidth - popupWidth) / 2);
    const top = Math.max(20, (viewportHeight - popupHeight) / 2);

    setInfoPopupPosition({ top, left });
    setIsInfoPopupVisible(true);
    setIsPopupVisible(false); // Close the options menu

    try {
      // Find the conversation in the list to get the image URL
      const existingConversation = conversations.find(conv => conv.threadId === threadId);

      if (isAuthenticated && user) {
        const token = await getAccessTokenSilently();

        const response = await axios.get(`${process.env.REACT_APP_SERVER}conversations/${threadId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // Find user in conversation
        const filteredUsers = response.data.users ? response.data.users.filter((user_conv) => {
          // Check for valid user object with ID and filter out current user and GPT
          const shouldInclude = user_conv && user_conv.id && user_conv.id !== user.sub && user_conv.userName !== "GPT";
          return shouldInclude;
        }) : [];

        if (filteredUsers.length === 0) {
          // If no other users found, try to use any user except the current user
          const anyOtherUser = response.data.users ? response.data.users.find(u => u.id !== user.sub) : null;

          if (anyOtherUser) {
            // Copy the image URL from the conversation list if it exists
            if (existingConversation && existingConversation.userIcon) {
              anyOtherUser.userIcon = existingConversation.userIcon;
            }
            setSelectedUser(anyOtherUser);
          } else if (response.data.users && response.data.users.length > 0) {
            // Last resort: just use the first user in the array
            const firstUser = response.data.users[0];
            // Copy the image URL from the conversation list if it exists
            if (existingConversation && existingConversation.userIcon) {
              firstUser.userIcon = existingConversation.userIcon;
            }
            setSelectedUser(firstUser);
          }
        } else {
          // Set temporary user and conversation for display only
          const tempUser = filteredUsers[0];
          // Copy the image URL from the conversation list if it exists
          if (existingConversation && existingConversation.userIcon) {
            tempUser.userIcon = existingConversation.userIcon;
          }
          setSelectedUser(tempUser);
        }

        setSelectedConversationDetails(response.data);

        // Also fetch analysis data for enriched profile info
        try {
          const analysisResp = await authenticatedAxios.get(`/analysis/case-options/${threadId}`);
          if (analysisResp.data?.analysis) {
            const profile = analysisResp.data.analysis.clientProfile || analysisResp.data.analysis.personalDetails || {};
            const summary = analysisResp.data.analysis.summary || {};
            setSelectedConversationDetails(prev => ({
              ...prev,
              _analysisProfile: profile,
              _analysisSummary: summary
            }));
          }
        } catch (e) {
          // Analysis not available, that's fine
        }
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error);
    }
  };

  const showConversationHistory = async (event) => {
    event.stopPropagation();

    if (!threadId) {
      return;
    }

    // Position the conversation history popup centered on screen
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const popupWidth = 450;
    const popupHeight = 600;

    const left = Math.max(20, (viewportWidth - popupWidth) / 2);
    const top = Math.max(20, (viewportHeight - popupHeight) / 2);

    setConversationHistoryPosition({ top, left });
    setIsPopupVisible(false); // Close the options menu

    try {
      if (isAuthenticated && user) {
        const token = await getAccessTokenSilently();

        const response = await axios.get(`${process.env.REACT_APP_SERVER}conversations/${threadId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // Find user in conversation
        const filteredUsers = response.data.users.filter((user_conv) => {
          // Fix: return true/false instead of user object
          return user_conv.userName !== "GPT" && user_conv.id !== user.sub;
        });

        // Set temporary user and conversation for display only
        // This doesn't affect the main conversation view
        const tempUser = filteredUsers[0];

        setSelectedUser(tempUser);
        setSelectedConversationDetails(response.data);
        setIsConversationHistoryVisible(true);
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error);
    }
  };

  // Function to group messages by date
  const groupMessagesByDate = (messages) => {
    if (!messages || !messages.length) return {};

    return messages.reduce((groups, message) => {
      const date = new Date(message.time).toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
      return groups;
    }, {});
  };

  const viewConversationQuick = async (event, threadId) => {
    event.preventDefault();
    event.stopPropagation();

    if (!threadId) {
      return;
    }

    // Position the conversation history popup centered on screen
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const popupWidth = 450;
    const popupHeight = 600;

    const left = Math.max(20, (viewportWidth - popupWidth) / 2);
    const top = Math.max(20, (viewportHeight - popupHeight) / 2);

    setConversationHistoryPosition({ top, left });

    try {
      if (isAuthenticated && user) {
        const token = await getAccessTokenSilently();

        const response = await axios.get(`${process.env.REACT_APP_SERVER}conversations/${threadId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // Find user in conversation
        const filteredUsers = response.data.users.filter((user_conv) => {
          // Fix: return true/false instead of user object
          return user_conv.userName !== "GPT" && user_conv.id !== user.sub;
        });

        // Set temporary user and conversation for display only
        // This doesn't affect the main conversation view
        const tempUser = filteredUsers[0];

        setSelectedUser(tempUser);
        setSelectedConversationDetails(response.data);
        setIsConversationHistoryVisible(true);
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error);
    }
  };

  const viewUserInfoQuick = async (event, threadId) => {
    event.preventDefault();
    event.stopPropagation();

    if (!threadId) {
      return;
    }

    // Position the info popup centered on screen
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const popupWidth = 380;
    const popupHeight = 450;

    const left = Math.max(20, (viewportWidth - popupWidth) / 2);
    const top = Math.max(20, (viewportHeight - popupHeight) / 2);

    setInfoPopupPosition({ top, left });

    try {
      if (isAuthenticated && user) {
        // First, find the user in our existing conversation list to get the image
        const existingConversation = conversations.find(conv => conv.threadId === threadId);

        const token = await getAccessTokenSilently();

        const response = await axios.get(`${process.env.REACT_APP_SERVER}conversations/${threadId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // Find user in conversation
        const filteredUsers = response.data.users ? response.data.users.filter((user_conv) => {
          // Check for valid user object with ID and filter out current user and GPT
          const shouldInclude = user_conv && user_conv.id && user_conv.id !== user.sub && user_conv.userName !== "GPT";
          return shouldInclude;
        }) : [];

        if (filteredUsers.length === 0) {
          // If no other users found, try to use any user except the current user
          const anyOtherUser = response.data.users ? response.data.users.find(u => u.id !== user.sub) : null;

          if (anyOtherUser) {
            // Copy the image URL from the conversation list if it exists
            if (existingConversation && existingConversation.userIcon) {
              anyOtherUser.userIcon = existingConversation.userIcon;
            }
            setSelectedUser(anyOtherUser);
          } else if (response.data.users && response.data.users.length > 0) {
            // Last resort: just use the first user in the array
            const firstUser = response.data.users[0];
            // Copy the image URL from the conversation list if it exists
            if (existingConversation && existingConversation.userIcon) {
              firstUser.userIcon = existingConversation.userIcon;
            }
            setSelectedUser(firstUser);
          }
        } else {
          // Set temporary user and conversation for display only
          const tempUser = filteredUsers[0];
          // Copy the image URL from the conversation list if it exists
          if (existingConversation && existingConversation.userIcon) {
            tempUser.userIcon = existingConversation.userIcon;
          }
          setSelectedUser(tempUser);
        }

        setSelectedConversationDetails(response.data);
        setIsInfoPopupVisible(true);
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error);
    }
  };

  const startConversationFromInfo = () => {
    setIsInfoPopupVisible(false);
    if (threadId && selectedUser) {
      handleConversationClick(threadId);
    }
  };

  const exportConversationHistory = () => {
    if (!selectedConversationDetails || !selectedConversationDetails.messages) return;

    try {
      // Format the conversation for export
      const userName = selectedUser?.userName || "User";
      let conversationText = `Conversation with ${userName}\n`;
      conversationText += `Started: ${new Date(selectedConversationDetails.createdAt).toLocaleString()}\n\n`;

      // Group messages by date
      const groupedMessages = groupMessagesByDate(selectedConversationDetails.messages);

      Object.entries(groupedMessages).forEach(([date, messages]) => {
        conversationText += `--- ${new Date(date).toLocaleDateString('en-US', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
        })} ---\n\n`;

        messages.forEach(message => {
          // Use the same sender logic as the display
          let sender;
          if (message.senderId === user.sub) {
            sender = 'You';
          } else if (message.type === 'system') {
            sender = 'iMavenAi';
          } else if (message.type === 'gpt' || message.type === 'bot') {
            sender = 'Eve Virtual Assistant';
          } else if (message.sender && message.sender !== message.senderId) {
            // Capitalize sender name (except iMavenAi)
            sender = message.sender === 'iMavenAi' ? 'iMavenAi' : 
              message.sender.split(' ').map(word => {
                if (word.length === 0) return word;
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
              }).join(' ');
          } else if (message.senderId && selectedConversationDetails.users) {
            const messageSender = selectedConversationDetails.users.find(u => u.id === message.senderId);
            if (messageSender) {
              const senderName = messageSender.userName || messageSender.name || messageSender.fullName || messageSender.displayName;
              sender = senderName === 'iMavenAi' ? 'iMavenAi' : 
                senderName.split(' ').map(word => {
                  if (word.length === 0) return word;
                  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                }).join(' ');
            } else {
              sender = userName;
            }
          } else if (message.content && (
            message.content.includes("I'm Eve") || 
            message.content.includes("Great to meet you") ||
            message.content.includes("How can I assist")
          )) {
            sender = 'Eve Virtual Assistant';
          } else {
            sender = userName;
          }

          const time = new Date(message.time).toLocaleTimeString('en-US', {
            hour: '2-digit', minute: '2-digit'
          });

          conversationText += `[${time}] ${sender}:\n${stripHtml(message.content)}\n\n`;
        });
      });

      // Create and download the text file
      const blob = new Blob([conversationText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Conversation_with_${userName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]
        }.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting conversation:', error);
    }
  };

  const exportConversationAsPdf = () => {
    if (!selectedConversationDetails || !selectedConversationDetails.messages) return;

    try {
      const userName = selectedUser?.userName || "User";
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      const maxWidth = pageWidth - margin * 2;
      let y = 20;

      const addPageIfNeeded = (requiredSpace = 12) => {
        if (y + requiredSpace > doc.internal.pageSize.getHeight() - 15) {
          doc.addPage();
          y = 20;
        }
      };

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`Conversation with ${userName}`, margin, y);
      y += 8;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      doc.text(`Started: ${new Date(selectedConversationDetails.createdAt).toLocaleString()}`, margin, y);
      y += 12;
      doc.setTextColor(0, 0, 0);

      const groupedMessages = groupMessagesByDate(selectedConversationDetails.messages);

      Object.entries(groupedMessages).forEach(([date, messages]) => {
        addPageIfNeeded(20);

        doc.setDrawColor(200, 200, 200);
        doc.line(margin, y, pageWidth - margin, y);
        y += 6;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 100, 100);
        doc.text(new Date(date).toLocaleDateString('en-US', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
        }), margin, y);
        y += 8;
        doc.setTextColor(0, 0, 0);

        messages.forEach(message => {
          let sender;
          if (message.senderId === user.sub) {
            sender = 'You';
          } else if (message.type === 'system') {
            sender = 'iMavenAi';
          } else if (message.type === 'gpt' || message.type === 'bot') {
            sender = 'Eve Virtual Assistant';
          } else if (message.sender && message.sender !== message.senderId) {
            sender = message.sender === 'iMavenAi' ? 'iMavenAi' :
              message.sender.split(' ').map(word => {
                if (word.length === 0) return word;
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
              }).join(' ');
          } else if (message.senderId && selectedConversationDetails.users) {
            const messageSender = selectedConversationDetails.users.find(u => u.id === message.senderId);
            if (messageSender) {
              const senderName = messageSender.userName || messageSender.name || messageSender.fullName || messageSender.displayName;
              sender = senderName === 'iMavenAi' ? 'iMavenAi' :
                senderName.split(' ').map(word => {
                  if (word.length === 0) return word;
                  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                }).join(' ');
            } else {
              sender = userName;
            }
          } else if (message.content && (
            message.content.includes("I'm Eve") ||
            message.content.includes("Great to meet you") ||
            message.content.includes("How can I assist")
          )) {
            sender = 'Eve Virtual Assistant';
          } else {
            sender = userName;
          }

          const time = new Date(message.time).toLocaleTimeString('en-US', {
            hour: '2-digit', minute: '2-digit'
          });

          const content = stripHtml(message.content);
          const wrappedContent = doc.splitTextToSize(content, maxWidth);
          const blockHeight = 6 + wrappedContent.length * 5;

          addPageIfNeeded(blockHeight + 4);

          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.text(`[${time}] ${sender}:`, margin, y);
          y += 5;

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          wrappedContent.forEach(line => {
            addPageIfNeeded(6);
            doc.text(line, margin, y);
            y += 5;
          });

          y += 4;
        });
      });

      doc.save(`Conversation_with_${userName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exporting conversation as PDF:', error);
    }
  };

  const handleRemoveUser = async () => {
    try {
      if (isAuthenticated && user && selectedUser && threadId) {
        // Update local state to hide the user from the list
        setConversations(prevConversations =>
          prevConversations.map(conv => {
            if (conv.threadId === threadId) {
              return { ...conv, isRemoved: true }; // Mark as removed instead of filtering out
            }
            return conv;
          })
        );

        // Also update filtered list
        setFilterdList(prevFilteredList =>
          prevFilteredList.map(conv => {
            if (conv.threadId === threadId) {
              return { ...conv, isRemoved: true }; // Mark as removed instead of filtering out
            }
            return conv;
          })
        );

        // Close confirmation dialog
        setIsRemoveConfirmVisible(false);
      }
    } catch (error) {
      console.error('Error marking user as removed:', error);
    }
  };

  // Handle conversation changes
  useEffect(() => {
    if (conversations && conversations.length > 0) {
      // If filters are active, reapply them when conversations change
      if (areFiltersActive) {
        applyFilters();
      } else {
        // Otherwise just show all conversations, without filtering for removed ones
        setFilterdList(conversations);
      }
    }
  }, [conversations, areFiltersActive]);

  // Update the useEffect for filter settings changes to prevent unnecessary filtering on initial load
  useEffect(() => {
    // Only process if there are conversations
    if (conversations && conversations.length > 0) {
      // Determine if any filters are active
      const hasActiveFilters =
        (filter && filter.length > 0) ||
        filterSettings.showOnlineOnly ||
        filterSettings.activityPeriod !== 'all';

      // Update the active filters state
      setAreFiltersActive(hasActiveFilters);

      // Only apply filters if there are actually active filters
      if (hasActiveFilters) {
        applyFilters();
      } else {
        // If no filters are active, show all conversations
        setFilterdList(conversations);
      }
    }
  }, [filterSettings, filter]);

  // Add a useEffect hook to force request data after a short delay if still loading
  useEffect(() => {
    let requestTimer = null;

    if (isLoading) {
      // If still loading after 500ms, re-request data
      requestTimer = setTimeout(() => {
        sendRequestUserList();
        console.log('Sending additional user list request after timeout');
      }, 500);
    }

    return () => {
      if (requestTimer) clearTimeout(requestTimer);
    };
  }, [isLoading]);

  // Debug logging for isOnline values during render
  useEffect(() => {
    // No debug logging needed
  }, [FilteredList]);

  const MIN_MESSAGES = 5;
  const shouldShowLeadConversation = useCallback((conversation) => {
    if (!conversation) return false;
    // Referral / manual leads are seeded with a single system message — always show
    // them; the min-message filter only exists to hide trivial widget sessions.
    if (conversation.leadType === 'referral' || conversation.leadType === 'manual') return true;
    return (conversation.messageCount || 0) >= MIN_MESSAGES || conversation.threadId == threadId;
  }, [threadId]);

  useEffect(() => {
    if (!threadId || activeTab !== 'leads') return;

    const timer = setTimeout(() => {
      const selectedLead = scrollContainerRef.current?.querySelector(`[data-thread-id="${threadId}"]`);
      selectedLead?.scrollIntoView({ block: 'nearest' });
    }, 50);

    return () => clearTimeout(timer);
  }, [threadId, activeTab, FilteredList]);
  
  const renderLst = FilteredList.filter(shouldShowLeadConversation).map((conversation, index) => {
    const isSelected = conversation.threadId == threadId;
    const isOnline = getUserOnlineStatus(conversation.id);
    const assignment = getAssignmentForThread(conversation.threadId);

    return <div key={index} data-thread-id={conversation.threadId} onClick={() => handleConversationClick(conversation.threadId)}>
      <S.UserContainer $isSelected={isSelected}>
        <UserAvatar name={getDisplayName(conversation)} userIcon={conversation.userIcon} size="46px" radius="10px" fontSize="17px" sizeSm="40px" fontSizeSm="15px" />
        <S.UserBody>
          <S.UserName title={getDisplayName(conversation)}>{getDisplayName(conversation)}</S.UserName>
          <S.UserChipsRow>
            {(() => {
              const label = getLeadLabelForThread(conversation.threadId);
              const cfg = label ? getLabelConfig(label) : null;
              return label ? (
                <span
                  onClick={(e) => openLabelPicker(e, conversation.threadId)}
                  style={{
                    background: cfg?.color || '#666', color: '#fff',
                    padding: '3px 10px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                    cursor: 'pointer', maxWidth: '150px', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0
                  }}
                  title={`Label: ${label} (click to change)`}
                >{label}</span>
              ) : (
                <span
                  onClick={(e) => openLabelPicker(e, conversation.threadId)}
                  style={{
                    border: '1px dashed rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.35)',
                    padding: '3px 10px', borderRadius: '4px', fontSize: '10px',
                    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0
                  }}
                  title="Set label"
                >+ Label</span>
              );
            })()}
            {(() => {
              const rows = assessmentsByThread[conversation.threadId];
              if (!rows || rows.length === 0) return null;
              // Show the most informative one: a published score beats a bare
              // verdict. `score` is null whenever the points grid could not
              // support a total — render that as "no score", never as 0.
              const best = rows.find(r => r.score != null) || rows[0];
              const meta = ASSESSMENT_CHIP_META[best.type] || { label: 'Assessment', colour: '#a5b4fc', bg: 'rgba(99,102,241,0.15)' };
              const scoreText = best.score != null
                ? `${best.score}/${best.maxScore}`
                : (best.verdict === 'appears-eligible' ? 'eligible'
                  : best.verdict === 'not-eligible' ? 'not eligible'
                  : 'no score');
              const others = rows.length > 1 ? ` (+${rows.length - 1} more)` : '';
              return (
                <span
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    background: meta.bg, color: meta.colour,
                    padding: '3px 9px', borderRadius: '5px', fontSize: '10px',
                    fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0
                  }}
                  title={`${meta.title}: ${scoreText}${best.verdict ? ` · ${best.verdict}` : ''}${others} — full breakdown is on the Case Dashboard`}
                >{meta.icon} {meta.label} {scoreText}</span>
              );
            })()}
            {(() => {
              const s = sharingSummary[conversation.threadId];
              if (!s || !s.total) return null;
              const parts = [];
              if (s.subs) parts.push(`${s.subs} subcontractor${s.subs > 1 ? 's' : ''}`);
              if (s.referrals) parts.push(`${s.referrals} referral${s.referrals > 1 ? 's' : ''}`);
              return (
                <span
                  onClick={(e) => openSharedWith(e, conversation)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    background: 'rgba(99, 102, 241, 0.15)', color: '#a5b4fc',
                    padding: '3px 9px', borderRadius: '5px', fontSize: '10px',
                    fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0
                  }}
                  title={`Shared with ${parts.join(' + ')} — click to manage`}
                >→ {s.total} shared</span>
              );
            })()}
            {conversation.shared && conversation.sharedFromName && (
              <span
                title={`Shared with you by ${conversation.sharedFromName}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '3px',
                  background: 'rgba(99, 102, 241, 0.15)', color: '#a5b4fc',
                  padding: '3px 8px', borderRadius: '4px', fontSize: '10px',
                  fontWeight: 600, maxWidth: '150px', overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}
              >↗ {conversation.sharedFromName}</span>
            )}
            {assignment && (
              <S.AssignedBadge title={`Assigned to ${assignment.assignedToName}`}>
                <span style={{ fontSize: '8px' }}>→</span>
                {assignment.assignedToName.split(' ')[0]}
              </S.AssignedBadge>
            )}
          </S.UserChipsRow>
        </S.UserBody>
        <S.UserRightCol>
          <S.UserRightTop>
            <S.UserWhen>{new Date(conversation.conversationStart).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              hour12: true
            })}</S.UserWhen>
            <S.DotsContainer
              className="dots-menu"
              style={{ flexShrink: 0 }}
              onClick={(event) => OpenMoreOptions(event, conversation.threadId)}
            >
              <S.Dot />
              <S.Dot />
              <S.Dot />
            </S.DotsContainer>
          </S.UserRightTop>
          {conversation.id?.includes('|') ? (
            <ImavenOnlineStatus isOnline={isOnline} />
          ) : (
            <span style={{ padding: '4px 8px', fontSize: '11px', color: '#f59e0b', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.25)', borderRadius: '6px', whiteSpace: 'nowrap' }} title="User has not signed up yet">N/A</span>
          )}
        </S.UserRightCol>
      </S.UserContainer>
      <S.LineContainer>
        <div></div>
      </S.LineContainer>
    </div>
  });

  // Render incomplete leads list — deduplicate by conversationId first
  const seenConvoIds = new Set();
  const dedupedIncomplete = incompleteConversations.filter(session => {
    const cid = session.conversationId;
    if (!cid) return true;
    if (seenConvoIds.has(cid)) return false;
    seenConvoIds.add(cid);
    return true;
  });

  const renderIncompleteList = dedupedIncomplete
    .filter(session => showHiddenIncomplete || !session.isHidden)
    .filter(session => (session.messageCount || 0) >= MIN_MESSAGES)
    .map((session, index) => {
      const hasEmail = session.userEmail && session.userEmail.trim();
      const hasPhone = session.userPhone && session.userPhone.trim();
      const hasName = session.userName && session.userName.trim();
      
      const isIncompleteSelected = selectedIncompleteId === (session.sessionId || session.conversationId);
      
      return (
        <S.IncompleteLeadContainer 
          key={session.sessionId || index} 
          $isHidden={session.isHidden}
          $isSelected={isIncompleteSelected}
          onClick={() => {
            if (session.conversationId && onIncompleteConversationSelect) {
              setSelectedIncompleteId(session.sessionId || session.conversationId);
              setThreadId(session.conversationId);
              onIncompleteConversationSelect(session);
            }
          }}
          style={{ 
            cursor: session.conversationId ? 'pointer' : 'default',
            opacity: session.conversationId ? (session.isHidden ? 0.6 : 1) : 0.5
          }}
          title={session.conversationId ? 'Click to view conversation' : 'No conversation data available'}
        >
          <S.IncompleteLeadHeader>
            <S.IncompleteLeadInfo>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <UserAvatar name={session.userName || session.userEmail || session.userPhone} />
                <S.IncompleteLeadContact>
                  {hasName && (
                    <S.ContactItem style={{ fontWeight: '500', marginBottom: '2px' }}>
                      {session.userName}
                    </S.ContactItem>
                  )}
                  {hasEmail && (
                    <S.ContactItem>
                      <span style={{ fontSize: '12px' }}>📧</span>
                      {session.userEmail}
                    </S.ContactItem>
                  )}
                  {hasPhone && (
                    <S.ContactItem>
                      <span style={{ fontSize: '12px' }}>📱</span>
                      {session.userPhone}
                    </S.ContactItem>
                  )}
                  {!hasEmail && !hasPhone && !hasName && (
                    <S.ContactItem style={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
                      No contact info
                    </S.ContactItem>
                  )}
                </S.IncompleteLeadContact>
              </div>
              <S.IncompleteLeadTime>
                Started {formatRelativeTime(session.conversationStartedAt)}
              </S.IncompleteLeadTime>
            </S.IncompleteLeadInfo>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {session.isHidden && <S.HiddenBadge>Hidden</S.HiddenBadge>}
              <S.DotsContainer
                className="dots-menu"
                onClick={(event) => openIncompleteMenu(event, session)}
                title="More options"
                style={{ cursor: 'pointer' }}
              >
                <S.Dot />
                <S.Dot />
                <S.Dot />
              </S.DotsContainer>
            </div>
          </S.IncompleteLeadHeader>
          {session.source?.referrer && (
            <div style={{ 
              fontSize: '10px', 
              color: 'rgba(255,255,255,0.35)', 
              marginTop: '4px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              From: {session.source.referrer}
            </div>
          )}
          <S.LineContainer>
            <div></div>
          </S.LineContainer>
        </S.IncompleteLeadContainer>
      );
    });

  const showRemoveConfirmation = (event) => {
    event.stopPropagation();

    if (!selectedUser || !threadId) {
      return;
    }

    // Position the remove confirmation dialog centered on screen
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const dialogWidth = 330;
    const dialogHeight = 180;

    const left = Math.max(20, (viewportWidth - dialogWidth) / 2);
    const top = Math.max(20, (viewportHeight - dialogHeight) / 2);

    setRemoveConfirmPosition({ top, left });
    setIsRemoveConfirmVisible(true);
    setIsPopupVisible(false); // Close the options menu
  };

  // Add sorting when updating conversations
  useEffect(() => {
    // Update cache whenever conversations change
    if (conversations && conversations.length > 0) {
      // Sort conversations by date before saving to cache
      const sortedConversations = [...conversations].sort((a, b) => {
        return new Date(b.conversationStart) - new Date(a.conversationStart);
      });

      userListCache.data = sortedConversations;
      userListCache.timestamp = Date.now();
    }
  }, [conversations]);

  // ---- Add Old User (legacy ZIP import) handlers ----
  const stopImportPolling = () => { importPollActiveRef.current = false; };

  // Poll the import job's progress until it finishes (backend has no socket).
  const pollImportStatus = (jobId) => {
    importPollActiveRef.current = true;
    const tick = async () => {
      if (!importPollActiveRef.current) return;
      try {
        const res = await authenticatedAxios.get(`/import/legacy/${jobId}`);
        const job = res.data?.job;
        if (job) {
          setImportJob(job);
          if (job.status === 'done' || job.status === 'failed') {
            importPollActiveRef.current = false;
            if (job.status === 'done') {
              showToast('Import complete — client added to your Leads', 'success');
              // Imported client is a real lead (completedAt set) -> refresh the Leads list.
              if (onRefreshConversations) onRefreshConversations();
              if (onFetchIncomplete) onFetchIncomplete(showHiddenIncomplete, inactiveThreshold);
            } else {
              showToast('Import failed — see details in the window', 'error');
            }
            return;
          }
        }
      } catch (e) {
        // transient error — keep polling
      }
      if (importPollActiveRef.current) setTimeout(tick, 1500);
    };
    tick();
  };

  const handleStartImport = async () => {
    if (!importFile) { showToast('Please choose a ZIP file first', 'error'); return; }
    setIsUploadingImport(true);
    setImportJob(null);
    setLinkSelections({});
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await authenticatedAxios.post('/import/legacy', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const jobId = res.data?.jobId;
      if (jobId) {
        setImportJob({ id: jobId, status: 'uploaded', progressPercent: 5, progressLog: [], originalZipName: importFile.name });
        pollImportStatus(jobId);
      } else {
        showToast('Failed to start import', 'error');
      }
    } catch (e) {
      showToast(e?.response?.data?.error || 'Failed to upload ZIP', 'error');
    } finally {
      setIsUploadingImport(false);
    }
  };

  const confirmImportLinks = async (keys) => {
    if (!importJob?.id) return;
    try {
      const res = await authenticatedAxios.post(`/import/legacy/${importJob.id}/confirm-links`, { requirementKeys: keys });
      if (res.data?.success) {
        showToast(`Linked ${res.data.applied?.length || 0} document(s) to requirements`, 'success');
        const s = await authenticatedAxios.get(`/import/legacy/${importJob.id}`);
        if (s.data?.job) setImportJob(s.data.job);
      }
    } catch (e) {
      showToast('Failed to confirm links', 'error');
    }
  };

  const handleDeleteImport = async () => {
    if (!importJob?.id) return;
    if (!window.confirm('Delete this imported client and ALL its files? This cannot be undone.')) return;
    try {
      await authenticatedAxios.delete(`/import/legacy/${importJob.id}`);
      showToast('Import deleted', 'success');
      if (onRefreshConversations) onRefreshConversations();
      if (onFetchIncomplete) onFetchIncomplete(showHiddenIncomplete, inactiveThreshold);
      closeImportModal();
    } catch (e) {
      showToast('Failed to delete import', 'error');
    }
  };

  const closeImportModal = () => {
    stopImportPolling();
    setIsAddOldUserModalVisible(false);
    setImportFile(null);
    setImportJob(null);
    setLinkSelections({});
  };

  // Hide the modal but keep the import running + polling in the background
  // (the floating progress widget stays visible so the adviser can keep working).
  const minimizeImportModal = () => setIsAddOldUserModalVisible(false);

  // Overlay / X handler: while a job is still running, minimize (keep it in the
  // background); once it's done/failed (or no job yet), fully close and reset.
  const dismissImportModal = () => {
    if (isUploadingImport) return;
    if (importJob && importJob.status !== 'done' && importJob.status !== 'failed') {
      minimizeImportModal();
    } else {
      closeImportModal();
    }
  };

  // Stop polling if the component unmounts mid-import.
  useEffect(() => () => { importPollActiveRef.current = false; }, []);

  // Auto-scroll the progress log to the newest line.
  useEffect(() => {
    if (importLogRef.current) importLogRef.current.scrollTop = importLogRef.current.scrollHeight;
  }, [importJob?.progressLog?.length]);

  return (
    <S.UserListContainer>
      <S.HeaderContainer>
        <S.HeaderTitle>
          <img src={UserListIcon} alt="Leads" />
          Leads
        </S.HeaderTitle>
        {onAddLead && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <S.AddLeadButton onClick={() => setIsAddLeadModalVisible(true)}>
              + Add Lead
            </S.AddLeadButton>
            <S.AddLeadButton
              onClick={() => setIsAddOldUserModalVisible(true)}
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
              title="Import a returning client from a backup ZIP"
            >
              + Add Returning Client
            </S.AddLeadButton>
          </div>
        )}
      </S.HeaderContainer>
      
      {/* Tabs for Leads vs Incomplete */}
      <S.TabContainer>
        <S.Tab 
          $active={activeTab === 'leads'} 
          onClick={() => handleTabChange('leads')}
        >
          Leads
          {FilteredList && FilteredList.filter(shouldShowLeadConversation).length > 0 && (
            <S.TabBadge $active={activeTab === 'leads'}>{FilteredList.filter(shouldShowLeadConversation).length}</S.TabBadge>
          )}
        </S.Tab>
        <S.Tab 
          $active={activeTab === 'incomplete'} 
          onClick={() => handleTabChange('incomplete')}
        >
          Incomplete
          {incompleteConversations && incompleteConversations.filter(s => (s.messageCount || 0) >= MIN_MESSAGES).length > 0 && (
            <S.TabBadge $active={activeTab === 'incomplete'}>{incompleteConversations.filter(s => (s.messageCount || 0) >= MIN_MESSAGES).length}</S.TabBadge>
          )}
        </S.Tab>
      </S.TabContainer>

      <div style={{
        position: "sticky",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'linear-gradient(180deg, rgba(25, 35, 55, 0.95) 0%, rgba(30, 40, 60, 0.9) 100%)',
        zIndex: '999',
        height: '60px',
        borderBottom: '1px solid rgba(100, 150, 200, 0.15)',
        padding: '0 15px'
      }}>
        <S.SearchBarWrapper>
          <img
            src={SearchIcon}
            alt="Search Icon"
          />
          <S.SearchInput
            type="text"
            placeholder="Search in user list..."
            value={filter}
            onChange={filterUserList}
          />
        </S.SearchBarWrapper>
        <S.FilterWrapper onClick={toggleFilterPopup} className="filter-icon" style={{ position: 'relative' }}>
          <img src={FilterIcon} alt="Filter Icon" />
          <S.FilterStatusBadge show={areFiltersActive} />
        </S.FilterWrapper>

        {isFilterPopupVisible && (
          <S.FilterPopup className="filter-popup">
            <S.FilterSection>
              <S.FilterTitle>Status</S.FilterTitle>
              <S.FilterOption onClick={() => handleFilterChange('showOnlineOnly', !filterSettings.showOnlineOnly)}>
                <S.FilterCheckbox checked={filterSettings.showOnlineOnly}>
                  {filterSettings.showOnlineOnly && (
                    <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                  )}
                </S.FilterCheckbox>
                <S.FilterLabel>Show only online users</S.FilterLabel>
              </S.FilterOption>
            </S.FilterSection>

            <S.FilterSection>
              <S.FilterTitle>Activity Period</S.FilterTitle>
              <S.FilterRadioOption onClick={() => handleFilterChange('activityPeriod', 'all')}>
                <S.FilterRadio checked={filterSettings.activityPeriod === 'all'} />
                <S.FilterLabel>All time</S.FilterLabel>
              </S.FilterRadioOption>
              <S.FilterRadioOption onClick={() => handleFilterChange('activityPeriod', 'today')}>
                <S.FilterRadio checked={filterSettings.activityPeriod === 'today'} />
                <S.FilterLabel>Today</S.FilterLabel>
              </S.FilterRadioOption>
              <S.FilterRadioOption onClick={() => handleFilterChange('activityPeriod', 'week')}>
                <S.FilterRadio checked={filterSettings.activityPeriod === 'week'} />
                <S.FilterLabel>Last 7 days</S.FilterLabel>
              </S.FilterRadioOption>
              <S.FilterRadioOption onClick={() => handleFilterChange('activityPeriod', 'month')}>
                <S.FilterRadio checked={filterSettings.activityPeriod === 'month'} />
                <S.FilterLabel>Last 30 days</S.FilterLabel>
              </S.FilterRadioOption>
            </S.FilterSection>

            {leadLabels.length > 0 && (
              <S.FilterSection>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                  <S.FilterTitle>
                    Lead Label
                    {labelFilterValues.length > 0 && (
                      <span style={{ color: 'rgba(255,255,255,0.45)', fontWeight: 400, fontSize: 12, marginLeft: 6 }}>
                        {labelFilterValues.length} selected
                      </span>
                    )}
                  </S.FilterTitle>
                  {labelFilterValues.length > 0 && (
                    <span
                      onClick={clearLabelFilter}
                      style={{ cursor: 'pointer', fontSize: 11, color: '#2f88f5' }}
                    >
                      Clear
                    </span>
                  )}
                </div>
                {/* Multi-select: pick as many labels as you like — a lead matches if it
                    has ANY of them (a lead only ever carries one label). */}
                <S.FilterOption onClick={clearLabelFilter}>
                  <S.FilterCheckbox checked={labelFilterValues.length === 0}>
                    {labelFilterValues.length === 0 && (
                      <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                    )}
                  </S.FilterCheckbox>
                  <S.FilterLabel>All labels</S.FilterLabel>
                </S.FilterOption>
                <S.FilterOption onClick={() => toggleLabelFilter('__none__')}>
                  <S.FilterCheckbox checked={labelFilterValues.includes('__none__')}>
                    {labelFilterValues.includes('__none__') && (
                      <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                    )}
                  </S.FilterCheckbox>
                  <S.FilterLabel>No label</S.FilterLabel>
                </S.FilterOption>
                {[...leadLabels].sort((a, b) => a.order - b.order).map(label => (
                  <S.FilterOption key={label.name} onClick={() => toggleLabelFilter(label.name)}>
                    <S.FilterCheckbox checked={labelFilterValues.includes(label.name)}>
                      {labelFilterValues.includes(label.name) && (
                        <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                      )}
                    </S.FilterCheckbox>
                    <S.FilterLabel>
                      <span style={{
                        display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%',
                        background: label.color, marginRight: '6px', verticalAlign: 'middle'
                      }} />
                      {label.name}
                    </S.FilterLabel>
                  </S.FilterOption>
                ))}
              </S.FilterSection>
            )}

            <S.FilterSection style={{ marginTop: 15 }}>
              <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                Current filters: {activeFilterSummary() || 'none'}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '11px', marginTop: 4 }}>
                {computeFilteredUsers().length} of {conversations?.length || 0} leads match
              </div>
            </S.FilterSection>

            <S.FilterButtons>
              <S.FilterButton onClick={resetFilters}>Reset</S.FilterButton>
              <S.FilterButton primary onClick={applyFilterSettings}>Apply</S.FilterButton>
            </S.FilterButtons>
          </S.FilterPopup>
        )}
      </div>
      <S.ScrollableContent ref={scrollContainerRef}>
        {areFiltersActive ? (
          <div style={{
            padding: '8px 15px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            fontSize: '13px',
            color: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'rgba(47, 136, 245, 0.05)'
          }}>
            <div>
              <span style={{ fontWeight: '500' }}>Active Filters:</span> {activeFilterSummary()}
              <span style={{ color: 'rgba(255,255,255,0.45)', marginLeft: 6 }}>
                ({FilteredList.length} of {conversations?.length || 0})
              </span>
            </div>
            <div
              onClick={resetFilters}
              style={{ cursor: 'pointer', fontSize: '12px', color: '#2f88f5', fontWeight: '500' }}
            >
              Clear all
            </div>
          </div>
        ) : null}

        {/* Render content based on active tab */}
        {activeTab === 'leads' ? (
          <>
            {/* Always show loading spinner when isLoading is true, regardless of other conditions */}
            {isLoading ? (
              <div style={{
                padding: '30px 20px',
                textAlign: 'center',
                color: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '15px',
                minHeight: '200px'
              }}>
                <div style={{
                  width: '30px',
                  height: '30px',
                  border: '3px solid rgba(255, 255, 255, 0.2)',
                  borderTop: '3px solid rgba(47, 136, 245, 0.8)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}></div>
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
                <div>Loading user list...</div>
              </div>
            ) : renderLst && renderLst.length > 0 ? (
              renderLst
            ) : shouldShowEmptyMessage ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>
                {conversationsInit && conversationsInit.length > 0 ?
                  "No users match the current filters." :
                  "No users available. The user list appears to be empty."}
              </div>
            ) : (
              // Show nothing while transitioning to prevent flash
              <div style={{ minHeight: '200px' }}></div>
            )}
          </>
        ) : (
          /* Incomplete tab content */
          <>
            {/* Filter controls for incomplete tab */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '8px', 
              padding: '12px 16px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              {/* Inactivity threshold selector */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                <span>Inactive for:</span>
                <select
                  value={inactiveThreshold}
                  onChange={(e) => setInactiveThreshold(Number(e.target.value))}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <option value={5} style={{ background: '#1a2332' }}>5 minutes</option>
                  <option value={15} style={{ background: '#1a2332' }}>15 minutes</option>
                  <option value={30} style={{ background: '#1a2332' }}>30 minutes</option>
                  <option value={60} style={{ background: '#1a2332' }}>1 hour</option>
                  <option value={120} style={{ background: '#1a2332' }}>2 hours</option>
                  <option value={1440} style={{ background: '#1a2332' }}>24 hours</option>
                </select>
              </div>
              
              {/* Show hidden toggle */}
              <S.ShowHiddenToggle style={{ padding: 0 }}>
                <input 
                  type="checkbox" 
                  checked={showHiddenIncomplete} 
                  onChange={(e) => setShowHiddenIncomplete(e.target.checked)}
                />
                Show hidden conversations
              </S.ShowHiddenToggle>
            </div>
            
            {incompleteLoading ? (
              <div style={{
                padding: '30px 20px',
                textAlign: 'center',
                color: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '15px',
                minHeight: '200px'
              }}>
                <div style={{
                  width: '30px',
                  height: '30px',
                  border: '3px solid rgba(255, 255, 255, 0.2)',
                  borderTop: '3px solid rgba(47, 136, 245, 0.8)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}></div>
                <div>Loading incomplete conversations...</div>
              </div>
            ) : renderIncompleteList && renderIncompleteList.length > 0 ? (
              renderIncompleteList
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>
                No incomplete conversations found.
                <div style={{ fontSize: '12px', marginTop: '8px', color: 'rgba(255,255,255,0.4)' }}>
                  Incomplete conversations appear here when users start a widget chat but don't finish.
                </div>
              </div>
            )}
          </>
        )}

        {isPopupVisible && (
          <S.PopupWindow
            className="popup-menu"
            top={popupPosition.top}
            left={popupPosition.left}
            onClick={(e) => e.stopPropagation()}
          >
            <S.PopupWindowItem onClick={showUserInfo}>
              <div style={{ float: 'left' }}>
                <span>Info</span>
              </div>
              <S.PopupIconWrapper>
                <img src={InfoIcon} alt='info icon' />
              </S.PopupIconWrapper>
            </S.PopupWindowItem>

            <S.LineContainer>
              <div style={{ height: "1px", width: "90%", backgroundColor: "rgba(255, 255, 255, 0.1)", borderRadius: "0" }}></div>
            </S.LineContainer>

            <S.PopupWindowItem onClick={showConversationHistory}>
              <div style={{ float: 'left' }}>
                <span>Conversation</span>
              </div>
              <S.PopupIconWrapper>
                <img src={ConversationIcon} alt='conversation icon' />
              </S.PopupIconWrapper>
            </S.PopupWindowItem>

            <S.LineContainer>
              <div style={{ height: "1px", width: "90%", backgroundColor: "rgba(255, 255, 255, 0.1)", borderRadius: "0" }}></div>
            </S.LineContainer>

            <S.PopupWindowItem onClick={() => {
              setIsFilesPopupVisible(true);
              setIsPopupVisible(false);
            }}>
              <div style={{ float: 'left' }}>
                <span>Files</span>
              </div>
              <S.PopupIconWrapper>
                <img src={AllFilesIcon} alt='files icon' />
              </S.PopupIconWrapper>
            </S.PopupWindowItem>

            <S.LineContainer>
              <div style={{ height: "1px", width: "90%", backgroundColor: "rgba(255, 255, 255, 0.1)", borderRadius: "0" }}></div>
            </S.LineContainer>

            {/* Assign Option */}
            <S.PopupWindowItem 
              style={{ position: 'relative' }}
              onMouseEnter={() => setIsAssignSubmenuVisible(true)}
              onMouseLeave={() => setIsAssignSubmenuVisible(false)}
            >
              <div style={{ float: 'left' }}>
                <span style={{ color: '#4caf50' }}>Assign</span>
              </div>
              <S.PopupIconWrapper>
                <span style={{ color: '#4caf50', fontSize: '14px' }}>→</span>
              </S.PopupIconWrapper>
              
              {/* Assignment Submenu */}
              {isAssignSubmenuVisible && (
                <S.AssignmentSubmenu 
                  onClick={(e) => e.stopPropagation()}
                  onMouseEnter={() => setIsAssignSubmenuVisible(true)}
                  onMouseLeave={() => setIsAssignSubmenuVisible(false)}
                >
                  <S.AssignmentSubmenuHeader>
                    Assign to
                  </S.AssignmentSubmenuHeader>
                  
                  {assignablePeople.length === 0 ? (
                    <div style={{ padding: '15px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                      No team members yet
                    </div>
                  ) : (
                    assignablePeople.map((person) => (
                      <S.AssignmentPersonItem 
                        key={person._id}
                        onClick={() => handleAssignCustomer(person._id, person.name)}
                        style={{ opacity: person.isPending ? 0.7 : 1 }}
                      >
                        <S.AssignmentPersonAvatar style={{
                          background: person.isPending 
                            ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
                            : person.memberRole === 'admin' 
                              ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                              : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)'
                        }}>
                          {person.name.charAt(0).toUpperCase()}
                        </S.AssignmentPersonAvatar>
                        <S.AssignmentPersonInfo>
                          <S.AssignmentPersonName>
                            {person.name}
                            {person.isPending && (
                              <span style={{ 
                                fontSize: '10px', 
                                background: 'rgba(251, 191, 36, 0.2)', 
                                color: '#fbbf24', 
                                padding: '2px 6px', 
                                borderRadius: '4px', 
                                marginLeft: '6px' 
                              }}>
                                PENDING
                              </span>
                            )}
                          </S.AssignmentPersonName>
                          <S.AssignmentPersonEmail>
                            {person.role || person.email}
                          </S.AssignmentPersonEmail>
                        </S.AssignmentPersonInfo>
                      </S.AssignmentPersonItem>
                    ))
                  )}
                  
                  <S.AddPersonButton onClick={() => {
                    setIsAddPersonModalVisible(true);
                    setIsAssignSubmenuVisible(false);
                    setIsPopupVisible(false);
                  }}>
                    <S.AddPersonIcon>+</S.AddPersonIcon>
                    <span>Add Team Member</span>
                  </S.AddPersonButton>
                </S.AssignmentSubmenu>
              )}
            </S.PopupWindowItem>

            <S.LineContainer>
              <div style={{ height: "1px", width: "90%", backgroundColor: "rgba(255, 255, 255, 0.1)", borderRadius: "0" }}></div>
            </S.LineContainer>

            {/* Share to subcontractor Option */}
            <S.PopupWindowItem
              style={{ position: 'relative' }}
              onMouseEnter={() => { setIsShareSubmenuVisible(true); loadSubcontractors(); }}
              onMouseLeave={() => setIsShareSubmenuVisible(false)}
            >
              <div style={{ float: 'left' }}>
                <span style={{ color: '#818cf8' }}>Share to subcontractor</span>
              </div>
              <S.PopupIconWrapper>
                <span style={{ color: '#818cf8', fontSize: '14px' }}>→</span>
              </S.PopupIconWrapper>
              {isShareSubmenuVisible && (
                <S.AssignmentSubmenu
                  onClick={(e) => e.stopPropagation()}
                  onMouseEnter={() => setIsShareSubmenuVisible(true)}
                  onMouseLeave={() => setIsShareSubmenuVisible(false)}
                >
                  <S.AssignmentSubmenuHeader>Share to subcontractor</S.AssignmentSubmenuHeader>
                  {loadingSubs ? (
                    <div style={{ padding: '15px', textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>Loading…</div>
                  ) : (subcontractors && subcontractors.length > 0) ? (
                    subcontractors.map((link) => (
                      <S.AssignmentPersonItem
                        key={link.partner?.expertId || link._id}
                        onClick={(e) => { handleShareCase(e, { threadId }, link.partner); setIsShareSubmenuVisible(false); setIsPopupVisible(false); }}
                      >
                        <S.AssignmentPersonInfo>
                          <S.AssignmentPersonName>{link.partner?.name || 'Subcontractor'}</S.AssignmentPersonName>
                        </S.AssignmentPersonInfo>
                      </S.AssignmentPersonItem>
                    ))
                  ) : (
                    <div style={{ padding: '15px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>No subcontractors yet</div>
                  )}
                </S.AssignmentSubmenu>
              )}
            </S.PopupWindowItem>

            {/* Shared with — who this case is shared with (subcontractors + referrals) */}
            <S.PopupWindowItem onClick={() => {
              const conv = FilteredList.find(c => c.threadId === threadId) || { threadId };
              openSharedWith(null, conv);
            }}>
              <div style={{ float: 'left' }}>
                <span style={{ color: '#a5b4fc' }}>Shared with…</span>
              </div>
              <S.PopupIconWrapper>
                <span style={{ color: '#a5b4fc', fontSize: '14px' }}>🔗</span>
              </S.PopupIconWrapper>
            </S.PopupWindowItem>

            <S.LineContainer>
              <div style={{ height: "1px", width: "90%", backgroundColor: "rgba(255, 255, 255, 0.1)", borderRadius: "0" }}></div>
            </S.LineContainer>

            {/* Send Email Option */}
            <S.PopupWindowItem onClick={() => openSendEmailModal(threadId, setIsPopupVisible)}>
              <div style={{ float: 'left' }}>
                <span style={{ color: '#8b5cf6' }}>Send Email</span>
              </div>
              <S.PopupIconWrapper>
                <span style={{ color: '#8b5cf6', fontSize: '14px' }}>✉</span>
              </S.PopupIconWrapper>
            </S.PopupWindowItem>

            {/* Signup Link - only for anonymous users */}
            {(() => {
              const conv = FilteredList.find(c => c.threadId === threadId);
              const isAnon = conv && !conv.id?.includes('|');
              return isAnon ? (
                <>
                  <S.LineContainer>
                    <div style={{ height: "1px", width: "90%", backgroundColor: "rgba(255, 255, 255, 0.1)", borderRadius: "0" }}></div>
                  </S.LineContainer>
                  <S.PopupWindowItem onClick={() => { setIsPopupVisible(false); handleGenerateLinkForLead(conv); }}>
                    <div style={{ float: 'left' }}>
                      <span style={{ color: '#14b8a6' }}>Signup Link</span>
                    </div>
                    <S.PopupIconWrapper>
                      <span style={{ color: '#14b8a6', fontSize: '14px' }}>🔗</span>
                    </S.PopupIconWrapper>
                  </S.PopupWindowItem>
                </>
              ) : null;
            })()}

          </S.PopupWindow>
        )}

        {/* ── "Shared with" panel — owner view of who a case is shared with ── */}
        {sharedWithPanel.visible && (
          <div
            onClick={closeSharedWith}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div
              className="shared-with-panel"
              onClick={(e) => e.stopPropagation()}
              style={{ width: '440px', maxWidth: '92vw', maxHeight: '82vh', overflowY: 'auto', background: '#131a24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', color: '#e6edf3' }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '15px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <span style={{ fontSize: '16px' }}>🔗</span>
                <span style={{ fontSize: '14px', fontWeight: 650 }}>Shared with</span>
                <span style={{ fontSize: '12px', color: '#6b7684', marginLeft: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }}>{sharedWithPanel.name}</span>
                <span onClick={closeSharedWith} style={{ cursor: 'pointer', color: '#8b98a5', fontSize: '18px', lineHeight: 1, padding: '0 2px' }}>×</span>
              </div>

              {sharedWithPanel.loading ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#8b98a5', fontSize: '13px' }}>Loading…</div>
              ) : sharedWithPanel.shares.length === 0 ? (
                <div style={{ padding: '28px 20px', textAlign: 'center', color: '#8b98a5', fontSize: '13px' }}>
                  This case isn’t shared with anyone yet.<br />
                  <span style={{ fontSize: '12px', color: '#6b7684' }}>Use “Share to subcontractor” or send a network referral to share it.</span>
                </div>
              ) : (
                <>
                  {/* Subcontractor group — live access, revocable */}
                  {sharedWithPanel.shares.some(s => s.type === 'subcontractor') && (
                    <div style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#a5b4fc', marginBottom: '10px' }}>
                        ⚙ Subcontractor <span style={{ color: '#6b7684', fontWeight: 600, letterSpacing: 0 }}>· live access</span>
                      </div>
                      {sharedWithPanel.shares.filter(s => s.type === 'subcontractor').map(share => (
                        <div key={share.id} style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '9px 0', borderTop: '1px dashed rgba(255,255,255,0.06)' }}>
                          <div style={{ flex: 'none', width: '34px', height: '34px', borderRadius: '9px', display: 'grid', placeItems: 'center', fontSize: '13px', fontWeight: 700, color: '#fff', background: getAvatarColor(share.name) }}>{getInitials(share.name)}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13.5px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{share.name}</div>
                            <div style={{ fontSize: '11.5px', color: '#8b98a5', marginTop: '2px' }}>
                              <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: '#3ED57D', marginRight: '5px' }}></span>
                              <span style={{ color: '#3ED57D', fontWeight: 600 }}>Active</span> · working the whole case
                            </div>
                          </div>
                          <span
                            onClick={() => handleRevokeShare(share)}
                            style={{ flex: 'none', fontSize: '11px', fontWeight: 600, padding: '5px 12px', borderRadius: '7px', cursor: revokingId ? 'default' : 'pointer', background: 'rgba(248,113,113,0.10)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)', opacity: revokingId ? 0.5 : 1 }}
                          >{revokingId === (share.grantId || share.id) ? '…' : 'Revoke'}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Referral group — consented data copy, cancel only before accept */}
                  {sharedWithPanel.shares.some(s => s.type === 'referral') && (
                    <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                      <div style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#d8b4fe', marginBottom: '10px' }}>
                        ↗ Referral <span style={{ color: '#6b7684', fontWeight: 600, letterSpacing: 0 }}>· data copy</span>
                      </div>
                      {sharedWithPanel.shares.filter(s => s.type === 'referral').map(share => (
                        <div key={share.id} style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '9px 0', borderTop: '1px dashed rgba(255,255,255,0.06)' }}>
                          <div style={{ flex: 'none', width: '34px', height: '34px', borderRadius: '9px', display: 'grid', placeItems: 'center', fontSize: '13px', fontWeight: 700, color: '#fff', background: getAvatarColor(share.name) }}>{getInitials(share.name)}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13.5px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{share.name}</div>
                            <div style={{ fontSize: '11.5px', color: '#8b98a5', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <span style={{ color: share.locked ? '#7aa2ff' : '#fbbf24', fontWeight: 600 }}>{share.statusLabel}</span>
                              {share.fieldCount ? <span style={{ color: '#6b7684' }}>· {share.fieldCount} field{share.fieldCount > 1 ? 's' : ''}</span> : null}
                            </div>
                          </div>
                          {share.revocable ? (
                            <span
                              onClick={() => handleRevokeShare(share)}
                              style={{ flex: 'none', fontSize: '11px', fontWeight: 600, padding: '5px 12px', borderRadius: '7px', cursor: revokingId ? 'default' : 'pointer', background: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.3)', opacity: revokingId ? 0.5 : 1 }}
                            >{revokingId === (share.referralId || share.id) ? '…' : 'Cancel'}</span>
                          ) : (
                            <span title="The consented fields were already copied into the partner's tenant and can't be recalled." style={{ flex: 'none', fontSize: '10.5px', color: '#6b7684', fontStyle: 'italic', whiteSpace: 'nowrap' }}>🔒 data copied</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Incomplete session 3-dots menu */}
        {incompleteMenuSession && (
          <S.PopupWindow
            className="incomplete-popup-menu"
            top={incompleteMenuPosition.top}
            left={incompleteMenuPosition.left}
            onClick={(e) => e.stopPropagation()}
            style={{ width: '200px', minHeight: 'auto' }}
          >
            <S.PopupWindowItem onClick={() => handleGenerateLink(incompleteMenuSession)}>
              <div style={{ float: 'left' }}>
                <span style={{ color: '#60a5fa' }}>Signup Link</span>
              </div>
              <S.PopupIconWrapper>
                <span style={{ fontSize: '14px' }}>🔗</span>
              </S.PopupIconWrapper>
            </S.PopupWindowItem>

            <S.LineContainer>
              <div style={{ height: "1px", width: "90%", backgroundColor: "rgba(255, 255, 255, 0.1)", borderRadius: "0" }}></div>
            </S.LineContainer>

            <S.PopupWindowItem onClick={() => handleConvertToLeadAction(incompleteMenuSession)}>
              <div style={{ float: 'left' }}>
                <span style={{ color: '#34d399' }}>Convert to Lead</span>
              </div>
              <S.PopupIconWrapper>
                <span style={{ fontSize: '14px' }}>✓</span>
              </S.PopupIconWrapper>
            </S.PopupWindowItem>

            <S.LineContainer>
              <div style={{ height: "1px", width: "90%", backgroundColor: "rgba(255, 255, 255, 0.1)", borderRadius: "0" }}></div>
            </S.LineContainer>

            <S.PopupWindowItem onClick={() => {
              if (incompleteMenuSession.isHidden) {
                handleUnhideSession(incompleteMenuSession.sessionId);
              } else {
                handleHideSession(incompleteMenuSession.sessionId);
              }
              setIncompleteMenuSession(null);
            }}>
              <div style={{ float: 'left' }}>
                <span>{incompleteMenuSession.isHidden ? 'Unhide' : 'Hide'}</span>
              </div>
              <S.PopupIconWrapper>
                <span style={{ fontSize: '14px' }}>{incompleteMenuSession.isHidden ? '👁' : '✕'}</span>
              </S.PopupIconWrapper>
            </S.PopupWindowItem>
          </S.PopupWindow>
        )}

        {/* Signup Link Dialog */}
        {signupLinkPopup.visible && (
          <>
            <S.SignupDialogOverlay onClick={() => setSignupLinkPopup({ visible: false, link: '', loading: false, email: '', userName: '', sendingEmail: false, emailSent: false, emailError: '' })} />
            <S.SignupLinkDialog onClick={(e) => e.stopPropagation()} className="signup-link-popup">
              <S.SignupDialogTitle>
                <span style={{ fontSize: '18px' }}>🔗</span>
                Signup Link
              </S.SignupDialogTitle>
              
              {signupLinkPopup.loading ? (
                <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.7)' }}>
                  Generating link...
                </div>
              ) : (
                <>
                  <S.SignupDialogField>
                    <label>Signup Link</label>
                    <S.SignupDialogLinkBox>
                      <span>{signupLinkPopup.link}</span>
                      <S.SignupDialogCopyBtn onClick={() => copyToClipboard(signupLinkPopup.link)}>
                        Copy
                      </S.SignupDialogCopyBtn>
                    </S.SignupDialogLinkBox>
                  </S.SignupDialogField>

                  <S.SignupDialogField>
                    <label>User Email</label>
                    <S.SignupDialogInput 
                      type="email"
                      value={signupLinkPopup.email}
                      onChange={(e) => setSignupLinkPopup(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="Enter user email"
                    />
                  </S.SignupDialogField>

                  <S.SignupDialogField>
                    <label>User Name</label>
                    <S.SignupDialogInput 
                      type="text"
                      value={signupLinkPopup.userName}
                      onChange={(e) => setSignupLinkPopup(prev => ({ ...prev, userName: e.target.value }))}
                      placeholder="Enter user name"
                    />
                  </S.SignupDialogField>

                  {signupLinkPopup.emailSent && (
                    <S.SignupDialogStatus>Email sent successfully!</S.SignupDialogStatus>
                  )}
                  {signupLinkPopup.emailError && (
                    <S.SignupDialogStatus $error>{signupLinkPopup.emailError}</S.SignupDialogStatus>
                  )}

                  <S.SignupDialogActions>
                    <S.SignupDialogSendBtn 
                      onClick={handleSendSignupEmail}
                      disabled={!signupLinkPopup.email || signupLinkPopup.sendingEmail || signupLinkPopup.emailSent}
                    >
                      {signupLinkPopup.sendingEmail ? 'Sending...' : signupLinkPopup.emailSent ? 'Sent!' : '✉ Send Email'}
                    </S.SignupDialogSendBtn>
                    <S.SignupDialogCloseBtn onClick={() => setSignupLinkPopup({ visible: false, link: '', loading: false, email: '', userName: '', sendingEmail: false, emailSent: false, emailError: '' })}>
                      Close
                    </S.SignupDialogCloseBtn>
                  </S.SignupDialogActions>
                </>
              )}
            </S.SignupLinkDialog>
          </>
        )}

        {/* Send Email Modal */}
        {isSendEmailModalVisible && sendEmailRecipient && (
          <S.SendEmailModalOverlay onClick={closeSendEmailModal}>
            <S.SendEmailModal onClick={(e) => e.stopPropagation()}>
              <S.SendEmailModalHeader>
                <h3>
                  <span style={{ fontSize: '18px' }}>✉️</span>
                  Send Email
                </h3>
                <S.CloseButton onClick={closeSendEmailModal}>✕</S.CloseButton>
              </S.SendEmailModalHeader>

              <S.SendEmailModalContent>
                {/* Recipient Info */}
                <S.EmailRecipientInfo>
                  <S.EmailRecipientAvatar>
                    {sendEmailRecipient.name.charAt(0).toUpperCase()}
                  </S.EmailRecipientAvatar>
                  <S.EmailRecipientDetails>
                    <span>{sendEmailRecipient.name}</span>
                    <span>{sendEmailRecipient.email}</span>
                  </S.EmailRecipientDetails>
                </S.EmailRecipientInfo>

                {/* Template Selection */}
                <S.EmailFormGroup>
                  <S.EmailFormLabel>Email Template</S.EmailFormLabel>
                  <S.EmailFormSelect
                    value={sendEmailForm.templateType}
                    onChange={(e) => handleSendEmailFormChange('templateType', e.target.value)}
                  >
                    <option value="manualLeadInvite">Invitation to Connect</option>
                    <option value="feedbackRequest">Request Feedback</option>
                    <option value="customMessage">Custom Message</option>
                  </S.EmailFormSelect>
                </S.EmailFormGroup>

                {/* Subject (for custom message) */}
                {sendEmailForm.templateType === 'customMessage' && (
                  <S.EmailFormGroup>
                    <S.EmailFormLabel>Subject</S.EmailFormLabel>
                    <S.EmailFormInput
                      type="text"
                      placeholder="Enter email subject..."
                      value={sendEmailForm.subject}
                      onChange={(e) => handleSendEmailFormChange('subject', e.target.value)}
                    />
                  </S.EmailFormGroup>
                )}

                {/* Personal Message */}
                <S.EmailFormGroup>
                  <S.EmailFormLabel>
                    {sendEmailForm.templateType === 'customMessage' ? 'Message' : 'Personal Message (Optional)'}
                  </S.EmailFormLabel>
                  <S.EmailFormTextarea
                    placeholder={
                      sendEmailForm.templateType === 'customMessage' 
                        ? "Write your message here..."
                        : "Add a personal note to include in the email..."
                    }
                    value={sendEmailForm.personalMessage}
                    onChange={(e) => handleSendEmailFormChange('personalMessage', e.target.value)}
                  />
                </S.EmailFormGroup>

                {/* Signature Templates Section */}
                <S.EmailFormGroup>
                  <S.EmailFormLabel style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>✍️</span>
                    Documents for Signature (Optional)
                    {!docuSignConnected && signatureTemplates.length > 0 && (
                      <span style={{ 
                        fontSize: '11px', 
                        color: '#f59e0b', 
                        marginLeft: '8px',
                        padding: '2px 8px',
                        background: 'rgba(245, 158, 11, 0.15)',
                        borderRadius: '4px'
                      }}>
                        DocuSign not connected
                      </span>
                    )}
                  </S.EmailFormLabel>
                  
                  {loadingTemplates ? (
                    <div style={{ 
                      padding: '16px', 
                      textAlign: 'center', 
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: '13px'
                    }}>
                      Loading templates...
                    </div>
                  ) : signatureTemplates.length === 0 ? (
                    <div style={{ 
                      padding: '16px', 
                      textAlign: 'center', 
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '13px',
                      border: '1px dashed rgba(255,255,255,0.2)',
                      borderRadius: '8px'
                    }}>
                      No signature templates yet.<br/>
                      <span style={{ fontSize: '12px' }}>
                        Add templates in Profile → E-Signature
                      </span>
                    </div>
                  ) : (
                    <S.SignatureTemplatesList>
                      {signatureTemplates.map(template => (
                        <S.SignatureTemplateItem 
                          key={template.id}
                          $selected={selectedSignatureTemplates.includes(template.id)}
                          $disabled={!docuSignConnected}
                          onClick={() => docuSignConnected && toggleSignatureTemplate(template.id)}
                          title={!docuSignConnected ? 'Connect DocuSign to send documents for signature' : template.name}
                        >
                          <S.SignatureTemplateIcon $type={template.fileType}>
                            {template.fileType === 'pdf' ? '📄' : '📝'}
                          </S.SignatureTemplateIcon>
                          <S.SignatureTemplateInfo>
                            <span>{template.name}</span>
                            <span>{template.fileType.toUpperCase()}</span>
                          </S.SignatureTemplateInfo>
                          <S.SignatureTemplateCheck $selected={selectedSignatureTemplates.includes(template.id)}>
                            {selectedSignatureTemplates.includes(template.id) && '✓'}
                          </S.SignatureTemplateCheck>
                        </S.SignatureTemplateItem>
                      ))}
                    </S.SignatureTemplatesList>
                  )}
                  
                  {selectedSignatureTemplates.length > 0 && (
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '8px 12px',
                      background: 'rgba(99, 102, 241, 0.1)',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.8)'
                    }}>
                      {selectedSignatureTemplates.length} document{selectedSignatureTemplates.length > 1 ? 's' : ''} will be sent via DocuSign for signature
                    </div>
                  )}
                </S.EmailFormGroup>

                {/* Status Messages */}
                {sendEmailStatus && (
                  sendEmailStatus.success ? (
                    <S.EmailSuccessMessage>
                      <span style={{ fontSize: '18px' }}>✓</span>
                      {sendEmailStatus.message}
                    </S.EmailSuccessMessage>
                  ) : (
                    <S.EmailErrorMessage>
                      <span style={{ fontSize: '18px' }}>✕</span>
                      {sendEmailStatus.message}
                    </S.EmailErrorMessage>
                  )
                )}
              </S.SendEmailModalContent>

              <S.SendEmailModalFooter>
                <S.EmailSenderInfo>
                  From: <span>support@imavenai.com</span>
                </S.EmailSenderInfo>
                <S.EmailButtonGroup>
                  <S.EmailCancelButton onClick={closeSendEmailModal}>
                    Cancel
                  </S.EmailCancelButton>
                  <S.SendEmailButton 
                    onClick={handleSendEmail}
                    disabled={isSendingEmail || sendEmailStatus?.success}
                  >
                    {isSendingEmail ? (
                      <>
                        <span style={{ animation: 'spin 1s linear infinite' }}>⌛</span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <span>✉</span>
                        Send Email
                      </>
                    )}
                  </S.SendEmailButton>
                </S.EmailButtonGroup>
              </S.SendEmailModalFooter>
            </S.SendEmailModal>
          </S.SendEmailModalOverlay>
        )}

        {isInfoPopupVisible && selectedUser && (
          <S.UserInfoPopup
            className="info-popup"
            style={{
              top: infoPopupPosition.top,
              left: infoPopupPosition.left
            }}
          >
            <S.PopupHeader>
              <S.PopupTitle>User Information</S.PopupTitle>
              <S.CloseButton onClick={() => setIsInfoPopupVisible(false)}>✕</S.CloseButton>
            </S.PopupHeader>
            <S.PopupContent>
              {(() => {
                const existingConv = conversations.find(c => c.threadId === threadId);
                const profile = selectedConversationDetails?._analysisProfile || {};
                const summary = selectedConversationDetails?._analysisSummary || {};
                const analyzedName = profile.fullName || summary.clientName ||
                  [profile.firstName || profile.givenName, profile.lastName || profile.familyName].filter(Boolean).join(' ') || '';
                const displayName = analyzedName || nameOverrides[threadId] || selectedUser.userName || selectedUser.name || selectedUser.full_name || selectedUser.displayName || "Unknown User";
                const email = profile.email || existingConv?.email || selectedUser.email || selectedUser.userEmail || selectedUser.mail || '';
                const phone = profile.phone || profile.phoneNumber || existingConv?.phone || existingConv?.phoneNumber || existingConv?.userPhone || selectedUser.phone || selectedUser.phoneNumber || '';
                return (<>
              <S.UserHeader>
                <S.UserHeaderImageWrapper>
                  <UserAvatar
                    name={displayName}
                    userIcon={selectedUser.userIcon}
                    size="64px"
                    radius="12px"
                    fontSize="24px"
                    sizeSm="54px"
                    fontSizeSm="20px"
                  />
                </S.UserHeaderImageWrapper>
                <S.UserHeaderInfo>
                  <S.UserHeaderName>{displayName}</S.UserHeaderName>
                  <S.UserHeaderStatus>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: getUserOnlineStatus(selectedUser.id) ? '#4caf50' : '#9e9e9e'
                    }}></div>
                    {getUserOnlineStatus(selectedUser.id) ? 'Online' : 'Offline'}
                  </S.UserHeaderStatus>
                </S.UserHeaderInfo>
              </S.UserHeader>

              <S.InfoSection>
                <S.InfoSectionTitle>User Details</S.InfoSectionTitle>
                <S.InfoRow>
                  <S.InfoLabel>Full Name</S.InfoLabel>
                  <S.InfoValue>{displayName}</S.InfoValue>
                </S.InfoRow>
                <S.InfoRow>
                  <S.InfoLabel>Email</S.InfoLabel>
                  <S.InfoValue>
                    {email ? (
                      <a
                        href={`mailto:${email}`}
                        style={{
                          color: '#2f88f5',
                          textDecoration: 'none',
                          transition: 'color 0.2s ease'
                        }}
                        onMouseOver={(e) => e.target.style.color = '#1c6fd4'}
                        onMouseOut={(e) => e.target.style.color = '#2f88f5'}
                      >
                        {email}
                      </a>
                    ) : (
                      "Not available"
                    )}
                  </S.InfoValue>
                </S.InfoRow>
                {phone && (
                  <S.InfoRow>
                    <S.InfoLabel>Phone</S.InfoLabel>
                    <S.InfoValue>{phone}</S.InfoValue>
                  </S.InfoRow>
                )}
                <S.InfoRow>
                  <S.InfoLabel>Status</S.InfoLabel>
                  <S.InfoValue>{getUserOnlineStatus(selectedUser.id) ? "Online" : "Offline"}</S.InfoValue>
                </S.InfoRow>
                <S.InfoRow>
                  <S.InfoLabel>Type</S.InfoLabel>
                  <S.InfoValue>{selectedUser.type || "user"}</S.InfoValue>
                </S.InfoRow>
                {selectedUser.role && (
                  <S.InfoRow>
                    <S.InfoLabel>Role</S.InfoLabel>
                    <S.InfoValue>{selectedUser.role}</S.InfoValue>
                  </S.InfoRow>
                )}
                {profile.nationality && (
                  <S.InfoRow>
                    <S.InfoLabel>Nationality</S.InfoLabel>
                    <S.InfoValue>{profile.nationality}</S.InfoValue>
                  </S.InfoRow>
                )}
              </S.InfoSection>
                </>);
              })()}

              <S.InfoSection>
                <S.InfoSectionTitle>Conversation Details</S.InfoSectionTitle>
                <S.InfoRow>
                  <S.InfoLabel>Started</S.InfoLabel>
                  <S.InfoValue>
                    {selectedConversationDetails?.createdAt ?
                      new Date(selectedConversationDetails.createdAt).toLocaleString() :
                      selectedConversationDetails?.messages?.length > 0 ?
                        new Date(selectedConversationDetails.messages[0].time).toLocaleString() :
                        "Not available"}
                  </S.InfoValue>
                </S.InfoRow>
                <S.InfoRow>
                  <S.InfoLabel>Message Count</S.InfoLabel>
                  <S.InfoValue>
                    {selectedConversationDetails?.messages?.length || 0}
                  </S.InfoValue>
                </S.InfoRow>
              </S.InfoSection>

              {selectedConversationDetails?.metadata && (
                <S.InfoSection>
                  <S.InfoSectionTitle>Additional Metadata</S.InfoSectionTitle>
                  {Object.entries(selectedConversationDetails.metadata).map(([key, value]) => (
                    <S.InfoRow key={key}>
                      <S.InfoLabel>{key}</S.InfoLabel>
                      <S.InfoValue>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</S.InfoValue>
                    </S.InfoRow>
                  ))}
                </S.InfoSection>
              )}

              <S.ActionsRow>
                <S.ActionButton
                  onClick={() => {
                    setIsInfoPopupVisible(false);
                    // Use the current popup user's threadId
                    viewConversationQuick(new Event('click'), selectedConversationDetails.threadId);
                  }}
                  color="#2f88f5"
                >
                  <img src={ConversationIcon} alt="Messages" />
                  View Messages
                </S.ActionButton>
              </S.ActionsRow>
            </S.PopupContent>
            <S.PopupFooter>
              <S.FooterButton onClick={() => setIsInfoPopupVisible(false)}>
                Close
              </S.FooterButton>
              <S.FooterButton primary onClick={() => {
                setIsInfoPopupVisible(false);
                // Use the current popup user's threadId for navigation
                if (selectedConversationDetails && selectedConversationDetails.threadId) {
                  handleConversationClick(selectedConversationDetails.threadId);
                }
              }}>
                Open Conversation
              </S.FooterButton>
            </S.PopupFooter>
          </S.UserInfoPopup>
        )}

        {isConversationHistoryVisible && selectedUser && selectedConversationDetails && (
          <S.ConversationHistoryPopup
            className="conversation-history-popup"
            style={{
              top: conversationHistoryPosition.top,
              left: conversationHistoryPosition.left
            }}
          >
            <S.PopupHeader>
              <S.PopupTitle>Conversation History with {selectedUser.userName || selectedUser.name || selectedUser.full_name || selectedUser.displayName || "User"}</S.PopupTitle>
              <S.CloseButton onClick={() => setIsConversationHistoryVisible(false)}>✕</S.CloseButton>
            </S.PopupHeader>
            <S.PopupContent>
              {selectedConversationDetails.messages && selectedConversationDetails.messages.length > 0 ? (
                <>
                  <S.ActionsRow>
                    <S.ActionButton onClick={exportConversationHistory} color="#4caf50">
                      <span>Export as Text</span>
                    </S.ActionButton>
                    <S.ActionButton onClick={exportConversationAsPdf} color="#2196f3">
                      <span>Export as PDF</span>
                    </S.ActionButton>
                  </S.ActionsRow>

                  {Object.entries(groupMessagesByDate(selectedConversationDetails.messages)).map(([date, messages]) => (
                    <div key={date}>
                      <S.DateDivider>
                        <span>{new Date(date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}</span>
                      </S.DateDivider>

                      {messages.map((message, index) => {
                        const isSelf = message.senderId === user.sub;
                        const isSystem = message.type === 'system';

                        // Helper function to capitalize names (except iMavenAi)
                        const capitalizeName = (name) => {
                          if (!name) return name;
                          if (name === 'iMavenAi') return 'iMavenAi'; // Preserve exact branding
                          
                          // Capitalize first letter of each word
                          return name.split(' ').map(word => {
                            if (word.length === 0) return word;
                            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                          }).join(' ');
                        };

                        // Helper function to determine the actual sender name
                        const getSenderName = () => {
                          // Special handling for video call messages
                          if (message.type === 'video-call' || (message.content && message.content.includes('/video-call/'))) {
                            return 'iMavenAi';
                          }
                          
                          if (isSelf) {
                            return 'You';
                          }
                          
                          // Check message type first
                          if (message.type === 'gpt' || message.type === 'bot') {
                            return 'Eve Virtual Assistant';
                          }
                          
                          if (message.type === 'system') {
                            return 'iMavenAi';
                          }
                          
                          // Check if message has sender information
                          if (message.sender && message.sender !== message.senderId) {
                            return capitalizeName(message.sender);
                          }
                          
                          // Try to find sender in conversation users
                          if (message.senderId && selectedConversationDetails.users) {
                            const messageSender = selectedConversationDetails.users.find(u => u.id === message.senderId);
                            if (messageSender) {
                              const senderName = messageSender.userName || messageSender.name || messageSender.fullName || messageSender.displayName;
                              return capitalizeName(senderName);
                            }
                          }
                          
                          // Check if it might be a GPT message based on content patterns
                          if (message.content && (
                            message.content.includes("I'm Eve") || 
                            message.content.includes("Great to meet you") ||
                            message.content.includes("How can I assist")
                          )) {
                            return 'Eve Virtual Assistant';
                          }
                          
                          // Fallback to selected user (the person you're chatting with)
                          const fallbackName = selectedUser.userName || selectedUser.name || 'User';
                          return capitalizeName(fallbackName);
                        };

                        if (isSystem) {
                          return (
                            <S.SystemMessage key={index}>
                              <S.MessageHeader>
                                <S.SystemMessageSender>iMavenAi</S.SystemMessageSender>
                                <S.MessageTime>
                                  {new Date(message.time).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </S.MessageTime>
                              </S.MessageHeader>
                              <S.MessageContent>{stripHtml(message.content)}</S.MessageContent>
                            </S.SystemMessage>
                          );
                        }

                        const senderName = getSenderName();

                        return (
                          <S.MessageContainer key={index} isSelf={isSelf} messageType={message.type}>
                            <S.MessageHeader>
                              <S.MessageSender isSelf={isSelf} messageType={message.type}>
                                {senderName}
                              </S.MessageSender>
                              <S.MessageTime>
                                {new Date(message.time).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </S.MessageTime>
                            </S.MessageHeader>
                            <S.MessageContent>
                              {message.type === 'video-call' || (message.content && message.content.includes('/video-call/')) 
                                ? 'Video call invitation sent' 
                                : stripHtml(message.content)
                              }
                            </S.MessageContent>
                          </S.MessageContainer>
                        );
                      })}
                    </div>
                  ))}
                </>
              ) : (
                <div style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', padding: '20px 0' }}>
                  No messages in this conversation yet.
                </div>
              )}
            </S.PopupContent>
            <S.PopupFooter>
              <S.FooterButton onClick={() => setIsConversationHistoryVisible(false)}>
                Close
              </S.FooterButton>
              <S.FooterButton primary onClick={() => {
                setIsConversationHistoryVisible(false);
                // Use the current popup user's threadId for navigation
                if (selectedConversationDetails && selectedConversationDetails.threadId) {
                  handleConversationClick(selectedConversationDetails.threadId);
                }
              }}>
                Open Conversation
              </S.FooterButton>
            </S.PopupFooter>
          </S.ConversationHistoryPopup>
        )}

        {isRemoveConfirmVisible && selectedUser && (
          <S.ConfirmDialog
            className="remove-confirm-dialog"
            style={{
              top: removeConfirmPosition.top,
              left: removeConfirmPosition.left
            }}
          >
            <S.ConfirmHeader>
              <S.ConfirmTitle>Hide User</S.ConfirmTitle>
            </S.ConfirmHeader>
            <S.ConfirmContent>
              Are you sure you want to hide <strong>{selectedUser.userName || selectedUser.name || "this user"}</strong> from your list?
              This conversation will no longer appear in your user list.
            </S.ConfirmContent>
            <S.ConfirmActions>
              <S.CancelButton onClick={() => setIsRemoveConfirmVisible(false)}>
                Cancel
              </S.CancelButton>
              <S.DeleteButton onClick={handleRemoveUser}>
                Hide
              </S.DeleteButton>
            </S.ConfirmActions>
          </S.ConfirmDialog>
        )}

        {isFilesPopupVisible && selectedUser && threadId && (
          <ConversationFilesPopup
            threadId={threadId}
            userName={selectedUser.userName || selectedUser.name || selectedUser.full_name || "User"}
            onClose={() => setIsFilesPopupVisible(false)}
          />
        )}

        {/* Add Person Modal */}
        {isAddPersonModalVisible && (() => {
          // Find the current conversation for context
          const currentConversation = conversations.find(c => c.threadId === threadId);
          const hasAssignmentContext = currentConversation && threadId;
          
          return (
            <>
              <S.ModalOverlay onClick={() => setIsAddPersonModalVisible(false)} />
              <S.AddPersonModal>
                <S.AddPersonModalHeader>
                  <S.AddPersonModalTitle>
                    {hasAssignmentContext ? 'Add & Assign Team Member' : 'Add Team Member'}
                  </S.AddPersonModalTitle>
                  <S.CloseButton onClick={() => setIsAddPersonModalVisible(false)}>✕</S.CloseButton>
                </S.AddPersonModalHeader>
                <S.AddPersonModalContent>
                  {/* Show assignment context if available */}
                  {hasAssignmentContext && (
                    <div style={{ 
                      fontSize: '13px', 
                      color: 'rgba(255,255,255,0.8)', 
                      marginBottom: '15px',
                      padding: '12px',
                      background: 'rgba(5, 150, 105, 0.15)',
                      borderRadius: '8px',
                      border: '1px solid rgba(5, 150, 105, 0.3)'
                    }}>
                      <strong style={{ color: '#10b981' }}>📋 Will be assigned to:</strong>
                      <p style={{ margin: '6px 0 0 0', fontWeight: '500' }}>
                        {currentConversation.name}
                      </p>
                    </div>
                  )}
                
                  <S.AddPersonInput
                    type="text"
                    placeholder="Full Name *"
                    value={newPersonName}
                    onChange={(e) => setNewPersonName(e.target.value)}
                    autoFocus
                  />
                  <S.AddPersonInput
                    type="email"
                    placeholder="Email Address *"
                    value={newPersonEmail}
                    onChange={(e) => setNewPersonEmail(e.target.value)}
                  />
                  <S.AddPersonInput
                    type="text"
                    placeholder="Job Title (optional, e.g. Paralegal)"
                    value={newPersonRole}
                    onChange={(e) => setNewPersonRole(e.target.value)}
                  />
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'rgba(255,255,255,0.6)', 
                    marginTop: '10px',
                    padding: '12px',
                    background: 'rgba(139, 92, 246, 0.1)',
                    borderRadius: '8px',
                    border: '1px solid rgba(139, 92, 246, 0.2)'
                  }}>
                    <strong style={{ color: '#a78bfa' }}>📧 What they'll receive:</strong>
                    <p style={{ margin: '8px 0 0 0' }}>
                      {hasAssignmentContext 
                        ? `An invitation email with info about ${currentConversation.name} as their first assignment.`
                        : 'An email invitation to join your team. They\'ll appear in your Profile → Team Management after accepting.'
                      }
                    </p>
                  </div>
                </S.AddPersonModalContent>
                <S.AddPersonModalFooter>
                  <S.ModalButton onClick={() => setIsAddPersonModalVisible(false)}>
                    Cancel
                  </S.ModalButton>
                  <S.ModalButton 
                    $primary 
                    onClick={handleAddPerson}
                    disabled={!newPersonName.trim() || !newPersonEmail.trim() || isAddingPerson}
                  >
                    {isAddingPerson 
                      ? 'Sending...' 
                      : hasAssignmentContext 
                        ? 'Send Invite & Assign' 
                        : 'Send Invitation'
                    }
                  </S.ModalButton>
                </S.AddPersonModalFooter>
              </S.AddPersonModal>
            </>
          );
        })()}
      </S.ScrollableContent>
      
      {/* Add Lead Modal */}
      {isAddLeadModalVisible && (
        <S.AddLeadModalOverlay onClick={() => {
          if (!isAddingLead) {
            setIsAddLeadModalVisible(false);
            setAddLeadSuccess(null);
          }
        }}>
          <S.AddLeadModal onClick={(e) => e.stopPropagation()}>
            <S.AddLeadModalHeader>
              <h3>{addLeadSuccess ? 'Lead Added Successfully!' : 'Add New Lead'}</h3>
              <S.CloseModalButton onClick={() => {
                setIsAddLeadModalVisible(false);
                setAddLeadSuccess(null);
                setAddLeadForm({ name: '', email: '', phone: '', notes: '' });
              }}>×</S.CloseModalButton>
            </S.AddLeadModalHeader>
            
            {addLeadSuccess ? (
              /* Success State - Show invite info */
              <>
                <S.AddLeadModalContent>
                  <div style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ color: 'rgba(34, 197, 94, 0.9)', fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}>✓</span> Invite Email Sent
                    </div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>
                      An invitation email has been sent to <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{addLeadSuccess.email}</strong>
                    </div>
                  </div>
                  
                  <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', marginBottom: '8px' }}>
                    The lead will receive a link to sign up. Once they complete the signup, they'll appear in your Leads list.
                  </div>
                  
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '11px', marginBottom: '6px' }}>
                      Signup Link (click to copy):
                    </div>
                    <div 
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '6px',
                        padding: '10px 12px',
                        fontSize: '11px',
                        color: 'rgba(47, 136, 245, 0.9)',
                        cursor: 'pointer',
                        wordBreak: 'break-all',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(addLeadSuccess.signupLink);
                        showToast('Link copied to clipboard!', 'success');
                      }}
                      onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.08)'}
                      onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.05)'}
                    >
                      {addLeadSuccess.signupLink}
                    </div>
                  </div>
                </S.AddLeadModalContent>
                <S.AddLeadModalFooter>
                  <S.ModalButton 
                    $primary 
                    onClick={() => {
                      setIsAddLeadModalVisible(false);
                      setAddLeadSuccess(null);
                      setAddLeadForm({ name: '', email: '', phone: '', notes: '' });
                    }}
                  >
                    Done
                  </S.ModalButton>
                </S.AddLeadModalFooter>
              </>
            ) : (
              /* Form State - Input fields */
              <>
                <S.AddLeadModalContent>
                  <S.AddLeadInput
                    type="text"
                    placeholder="Name *"
                    value={addLeadForm.name}
                    onChange={(e) => setAddLeadForm(prev => ({ ...prev, name: e.target.value }))}
                    autoFocus
                  />
                  <S.AddLeadInput
                    type="email"
                    placeholder="Email *"
                    value={addLeadForm.email}
                    onChange={(e) => setAddLeadForm(prev => ({ ...prev, email: e.target.value }))}
                  />
                  <S.AddLeadInput
                    type="tel"
                    placeholder="Phone (optional)"
                    value={addLeadForm.phone}
                    onChange={(e) => setAddLeadForm(prev => ({ ...prev, phone: e.target.value }))}
                  />
                  <S.AddLeadTextarea
                    placeholder="Notes (optional) - Will be included in the invite email"
                    value={addLeadForm.notes}
                    onChange={(e) => setAddLeadForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                  />
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'rgba(255, 255, 255, 0.5)', 
                    marginTop: '8px',
                    padding: '10px',
                    background: 'rgba(47, 136, 245, 0.08)',
                    borderRadius: '6px',
                    border: '1px solid rgba(47, 136, 245, 0.15)'
                  }}>
                    An invitation email will be sent to the lead with a link to sign up and connect with you.
                  </div>
                </S.AddLeadModalContent>
                <S.AddLeadModalFooter>
                  <S.ModalButton onClick={() => {
                    setIsAddLeadModalVisible(false);
                    setAddLeadForm({ name: '', email: '', phone: '', notes: '' });
                  }}>
                    Cancel
                  </S.ModalButton>
                  <S.ModalButton 
                    $primary 
                    onClick={async () => {
                      if (!addLeadForm.name.trim() || !addLeadForm.email.trim()) {
                        showToast('Please enter name and email', 'error');
                        return;
                      }
                      
                      setIsAddingLead(true);
                      try {
                        const result = await onAddLead(addLeadForm);
                        if (result && result.success) {
                          setAddLeadSuccess({
                            signupLink: result.signupLink,
                            email: addLeadForm.email
                          });
                          setAddLeadForm({ name: '', email: '', phone: '', notes: '' });
                        } else {
                          showToast('Failed to add lead', 'error');
                        }
                      } catch (error) {
                        showToast('Failed to add lead', 'error');
                      } finally {
                        setIsAddingLead(false);
                      }
                    }}
                    disabled={!addLeadForm.name.trim() || !addLeadForm.email.trim() || isAddingLead}
                  >
                    {isAddingLead ? 'Adding...' : 'Add & Send Invite'}
                  </S.ModalButton>
                </S.AddLeadModalFooter>
              </>
            )}
          </S.AddLeadModal>
        </S.AddLeadModalOverlay>
      )}

      {/* Add Old User (legacy ZIP import) Modal */}
      {isAddOldUserModalVisible && (
        <S.AddLeadModalOverlay onClick={() => dismissImportModal()}>
          <S.AddLeadModal onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px', width: '92%' }}>
            <S.AddLeadModalHeader>
              <h3>Add Returning Client — Import from Backup</h3>
              <S.CloseModalButton onClick={dismissImportModal}>×</S.CloseModalButton>
            </S.AddLeadModalHeader>

            <S.AddLeadModalContent>
              {/* Step 1: choose the ZIP (before an import job exists) */}
              {!importJob && (
                <>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', marginBottom: '10px' }}>
                    Upload a returning client's backup ZIP. The system will extract every file,
                    read the documents, create the client with all extracted info, and store all
                    files under them — ready to continue working.
                  </div>
                  <input
                    ref={importFileInputRef}
                    type="file"
                    accept=".zip,application/zip,application/x-zip-compressed"
                    style={{ display: 'none' }}
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  />
                  <div
                    onClick={() => importFileInputRef.current?.click()}
                    style={{
                      border: '1px dashed rgba(255,255,255,0.25)', borderRadius: '8px',
                      padding: '18px', textAlign: 'center', cursor: 'pointer',
                      background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.7)', fontSize: '13px'
                    }}
                  >
                    {importFile ? (
                      <span>📦 <strong>{importFile.name}</strong> ({Math.round(importFile.size / 1024)} KB) — click to change</span>
                    ) : (
                      <span>Click to choose a .zip file</span>
                    )}
                  </div>
                </>
              )}

              {/* Step 2+: live progress */}
              {importJob && (
                <>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '6px', wordBreak: 'break-all' }}>
                    📦 {importJob.originalZipName}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>
                    <span>Status: <strong style={{ color: importJob.status === 'failed' ? '#ef4444' : importJob.status === 'done' ? '#22c55e' : '#2f88f5' }}>{importJob.status}</strong></span>
                    <span>{importJob.progressPercent || 0}%</span>
                  </div>
                  <div style={{ height: '8px', background: 'rgba(255,255,255,0.08)', borderRadius: '4px', overflow: 'hidden', marginBottom: '10px' }}>
                    <div style={{ height: '100%', width: `${importJob.progressPercent || 0}%`, background: importJob.status === 'failed' ? '#ef4444' : '#2f88f5', transition: 'width .3s ease' }} />
                  </div>

                  {/* Live step log — repeats each step the system performs */}
                  <div
                    ref={importLogRef}
                    style={{ maxHeight: '220px', overflowY: 'auto', background: 'rgba(0,0,0,0.25)', borderRadius: '6px', padding: '8px 10px', fontSize: '11.5px', fontFamily: 'monospace', lineHeight: 1.5 }}
                  >
                    {(importJob.progressLog || []).length === 0 && (
                      <div style={{ color: 'rgba(255,255,255,0.4)' }}>Starting…</div>
                    )}
                    {(importJob.progressLog || []).map((l, i) => (
                      <div key={i} style={{ color: l.level === 'error' ? '#ef4444' : l.level === 'warn' ? '#f59e0b' : 'rgba(255,255,255,0.75)' }}>
                        {l.message}
                      </div>
                    ))}
                  </div>

                  {importJob.status === 'failed' && importJob.error && (
                    <div style={{ marginTop: '10px', color: '#ef4444', fontSize: '12px' }}>Error: {importJob.error}</div>
                  )}

                  {/* Done: summary + confirmable suggested requirement links */}
                  {importJob.status === 'done' && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginBottom: '8px' }}>
                        ✓ Client <strong>{importJob.clientName || 'Imported Client'}</strong>
                        {importJob.clientEmail ? ` · ${importJob.clientEmail}` : ''} · stored {importJob.storedFiles}/{importJob.totalFiles} files
                        {importJob.failedFiles?.length ? ` · ${importJob.failedFiles.length} failed` : ''}
                      </div>

                      {importJob.suggestedLinks?.length > 0 && (
                        <div style={{ background: 'rgba(47,136,245,0.06)', border: '1px solid rgba(47,136,245,0.15)', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
                          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '6px' }}>
                            Suggested document → requirement links (review &amp; confirm):
                          </div>
                          {importJob.suggestedLinks.map((s, i) => {
                            const checked = linkSelections[s.requirementKey] !== false && s.status === 'pending';
                            return (
                              <label key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '4px 0', cursor: s.status === 'pending' ? 'pointer' : 'default', opacity: s.status === 'pending' ? 1 : 0.6 }}>
                                <input
                                  type="checkbox"
                                  checked={s.status === 'confirmed' ? true : checked}
                                  disabled={s.status !== 'pending'}
                                  onChange={() => setLinkSelections(p => ({ ...p, [s.requirementKey]: !checked }))}
                                  style={{ marginTop: '2px' }}
                                />
                                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>
                                  <strong>{s.fileName}</strong> → {s.name} {s.status === 'confirmed' ? '✓' : ''}
                                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px' }}>
                                    {Math.round((s.confidence || 0) * 100)}% · {s.reason}
                                  </div>
                                </span>
                              </label>
                            );
                          })}
                          {importJob.suggestedLinks.some(s => s.status === 'pending') && (
                            <S.ModalButton
                              $primary
                              style={{ marginTop: '6px' }}
                              onClick={() => {
                                const keys = importJob.suggestedLinks
                                  .filter(s => s.status === 'pending' && linkSelections[s.requirementKey] !== false)
                                  .map(s => s.requirementKey);
                                confirmImportLinks(keys);
                              }}
                            >
                              Confirm selected links
                            </S.ModalButton>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </S.AddLeadModalContent>

            <S.AddLeadModalFooter>
              {!importJob && (
                <>
                  <S.ModalButton onClick={closeImportModal}>Cancel</S.ModalButton>
                  <S.ModalButton $primary onClick={handleStartImport} disabled={!importFile || isUploadingImport}>
                    {isUploadingImport ? 'Uploading…' : 'Start Import'}
                  </S.ModalButton>
                </>
              )}
              {importJob && importJob.status === 'done' && (
                <>
                  <S.ModalButton onClick={handleDeleteImport} style={{ color: '#ef4444' }}>Delete import (undo)</S.ModalButton>
                  <S.ModalButton $primary onClick={closeImportModal}>Done</S.ModalButton>
                </>
              )}
              {importJob && importJob.status === 'failed' && (
                <S.ModalButton $primary onClick={closeImportModal}>Close</S.ModalButton>
              )}
              {importJob && importJob.status !== 'done' && importJob.status !== 'failed' && (
                <S.ModalButton onClick={minimizeImportModal}>Run in background</S.ModalButton>
              )}
            </S.AddLeadModalFooter>
          </S.AddLeadModal>
        </S.AddLeadModalOverlay>
      )}

      {/* Floating background-import progress widget — shown while the modal is
          minimized so the adviser can keep working and still see progress. */}
      {importJob && !isAddOldUserModalVisible && (
        <div
          onClick={() => setIsAddOldUserModalVisible(true)}
          title="Click to expand the import"
          style={{
            position: 'fixed', bottom: '20px', right: '20px', zIndex: 99998,
            width: '300px', cursor: 'pointer',
            background: 'linear-gradient(180deg, rgba(30,35,50,0.98) 0%, rgba(22,28,42,0.99) 100%)',
            border: '1px solid rgba(100,150,200,0.2)', borderRadius: '10px',
            boxShadow: '0 6px 24px rgba(0,0,0,0.45)', padding: '12px 14px'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#fff' }}>
              {importJob.status === 'done'
                ? '✓ Import complete'
                : importJob.status === 'failed'
                  ? '✗ Import failed'
                  : 'Importing returning client…'}
            </span>
            <span
              onClick={(e) => { e.stopPropagation(); closeImportModal(); }}
              title="Dismiss"
              style={{ cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: '16px', lineHeight: 1, padding: '0 2px' }}
            >
              ×
            </span>
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', marginBottom: '6px', wordBreak: 'break-all' }}>
            📦 {importJob.originalZipName}
          </div>
          <div style={{ height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${importJob.progressPercent || 0}%`,
              background: importJob.status === 'failed' ? '#ef4444' : importJob.status === 'done' ? '#22c55e' : '#2f88f5',
              transition: 'width .3s ease'
            }} />
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '5px' }}>
            {importJob.status === 'done'
              ? 'Click to review'
              : importJob.status === 'failed'
                ? 'Click for details'
                : `${importJob.progressPercent || 0}% · click to expand`}
          </div>
        </div>
      )}

      {/* Label Picker Dropdown */}
      {labelPickerThreadId && (
        <>
          <div 
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }}
            onClick={() => setLabelPickerThreadId(null)}
          />
          <div style={{
            position: 'fixed',
            top: labelPickerPosition.top,
            left: labelPickerPosition.left,
            width: '200px',
            maxHeight: '340px',
            overflowY: 'auto',
            background: 'linear-gradient(180deg, rgba(30, 35, 50, 0.98) 0%, rgba(22, 28, 42, 0.99) 100%)',
            border: '1px solid rgba(100, 150, 200, 0.2)',
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
            zIndex: 100000,
            padding: '6px 0'
          }}>
            <div style={{ padding: '6px 12px', fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Set Label
            </div>
            <div
              onClick={() => handleSetLeadLabel(labelPickerThreadId, null)}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', border: '1px dashed rgba(255,255,255,0.3)', display: 'inline-block' }} />
              No label
            </div>
            {leadLabels.sort((a, b) => a.order - b.order).map(label => (
              <div
                key={label.name}
                onClick={() => handleSetLeadLabel(labelPickerThreadId, label.name)}
                style={{ 
                  padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: '#fff',
                  display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.15s',
                  background: getLeadLabelForThread(labelPickerThreadId) === label.name ? 'rgba(255,255,255,0.08)' : 'transparent'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = getLeadLabelForThread(labelPickerThreadId) === label.name ? 'rgba(255,255,255,0.08)' : 'transparent'}
              >
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: label.color, display: 'inline-block', flexShrink: 0 }} />
                {label.name}
                {getLeadLabelForThread(labelPickerThreadId) === label.name && (
                  <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>✓</span>
                )}
              </div>
            ))}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '4px', paddingTop: '4px' }}>
              <div
                onClick={() => { setLabelPickerThreadId(null); setEditLabelsData([...leadLabels]); setIsEditLabelsVisible(true); }}
                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '12px', color: '#2f88f5', display: 'flex', alignItems: 'center', gap: '6px' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(47, 136, 245, 0.08)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                ✏️ Edit Labels
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Labels Modal */}
      {isEditLabelsVisible && (
        <S.AddLeadModalOverlay onClick={() => setIsEditLabelsVisible(false)}>
          <S.AddLeadModal onClick={e => e.stopPropagation()} style={{ maxWidth: '480px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <S.AddLeadModalHeader>
              <h3>Edit Lead Labels</h3>
              <S.CloseModalButton onClick={() => setIsEditLabelsVisible(false)}>×</S.CloseModalButton>
            </S.AddLeadModalHeader>
            <S.AddLeadModalContent style={{ overflowY: 'auto', flex: 1, gap: '8px' }}>
              {editLabelsData.sort((a, b) => a.order - b.order).map((label, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0' }}>
                  <input 
                    type="color" 
                    value={label.color} 
                    onChange={e => {
                      const updated = [...editLabelsData];
                      updated[idx] = { ...updated[idx], color: e.target.value };
                      setEditLabelsData(updated);
                    }}
                    style={{ width: '32px', height: '32px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: 'transparent', padding: 0 }}
                  />
                  <S.AddLeadInput
                    value={label.name}
                    onChange={e => {
                      const updated = [...editLabelsData];
                      updated[idx] = { ...updated[idx], name: e.target.value };
                      setEditLabelsData(updated);
                    }}
                    style={{ flex: 1, marginBottom: 0 }}
                    placeholder="Label name"
                  />
                  <button
                    onClick={() => {
                      const updated = editLabelsData.filter((_, i) => i !== idx).map((l, i) => ({ ...l, order: i }));
                      setEditLabelsData(updated);
                    }}
                    style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', color: '#f87171', padding: '6px 10px', cursor: 'pointer', fontSize: '13px' }}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  setEditLabelsData(prev => [...prev, { name: '', color: '#6366f1', order: prev.length }]);
                }}
                style={{ 
                  background: 'transparent', border: '1px dashed rgba(47,136,245,0.4)', borderRadius: '8px',
                  color: '#2f88f5', padding: '10px', cursor: 'pointer', fontSize: '13px', marginTop: '4px', width: '100%'
                }}
              >
                + Add Label
              </button>
            </S.AddLeadModalContent>
            <S.AddLeadModalFooter>
              <S.ModalButton onClick={() => setIsEditLabelsVisible(false)}>Cancel</S.ModalButton>
              <S.ModalButton 
                $primary 
                onClick={handleSaveLabels}
                disabled={editLabelsData.some(l => !l.name.trim())}
              >
                Save Labels
              </S.ModalButton>
            </S.AddLeadModalFooter>
          </S.AddLeadModal>
        </S.AddLeadModalOverlay>
      )}
    </S.UserListContainer>
  );
};

export default UserList;
